import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TablePagination,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Layers, FileText, XCircle, Upload } from "lucide-react";
import NotoSansRegular from "../../fonts/NotoSans-Regular.ttf";
import NotoSansBold from "../../fonts/NotoSans-Bold.ttf";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import blankA4 from "../../pdf/blankA4.pdf";
import noData from "../../images/noData.jpg";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import verifyImg from "../../images/check-mark.png";
import Avatar from "@mui/material/Avatar";
import CircleIcon from "@mui/icons-material/Circle";
import PrintIcon from "@mui/icons-material/Print";
import Chip from "@mui/material/Chip";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import { BASE_URL, TARGET_TYPE } from "../../constants";
import { appendSanitizedReportHtml } from "../../utils/reportSanitizer";
import { useNavigate } from "react-router-dom";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import html2canvas from "html2canvas";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import QRCode from "qrcode";
import dayjs from "dayjs";
import CircularProgress from "@mui/material/CircularProgress";
import { invoice, finalResult, signDoctors } from "../../constants";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
// import QuillToText from "./QuillToText";
import fontkit from "@pdf-lib/fontkit";
import BarcodeFont from "./free3of9/fre3of9x.ttf";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
// import QRfont from "./fonts/QR-font-tfb.ttf";
import { useRecoilState, useRecoilValue } from "recoil";
import { authState } from "../../recoil/authAtom";
import Log from "../logDialog/Log";
import { Delete } from "@mui/icons-material";
import JsBarcode from "jsbarcode";

import {
  pathologyReportState,
  pathologyReportsLoadedState,
  rowLoadingState,
  formattedFromDateState,
  formattedToDateState,
  fromDateState,
  toDateState,
} from "../../recoil/pathologyReportState";
import { toast } from "react-toastify";
const getFormattedCurrentDate = () => {
  const today = new Date();
  const day = today.getDate();
  const month = today.toLocaleString("en-US", { month: "long" });
  const year = today.getFullYear();
  const formattedDay = day < 10 ? `0${day}` : day;

  return `${month} ${formattedDay}, ${year}`;
};
const PathologyReports = () => {
  const navigate = useNavigate();
  const user = useRecoilValue(authState);
  // const [pathologies, setPathologies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [uploadPathology, setUploadPathology] = useState(null);

  const [pathologyReportsLoaded, setPathologyReportsLoaded] = useRecoilState(
    pathologyReportsLoadedState,
  );
  const [pathologyReport, setPathologyReport] =
    useRecoilState(pathologyReportState);
  const [fromDate, setFromDate] = useRecoilState(fromDateState);
  const [toDate, setToDate] = useRecoilState(toDateState);
  const [rowLoading, setRowLoading] = useRecoilState(rowLoadingState);
  const [formattedFromDate, setFormattedFromDate] = useRecoilState(
    formattedFromDateState,
  );
  const [formattedToDate, setFormattedToDate] =
    useRecoilState(formattedToDateState);

  const showAgeInReport = (dob) => {
    const birthDate = dayjs(dob, "MMMM DD, YYYY");
    const today = dayjs();

    const years = today.diff(birthDate, "year");
    const months = today.diff(birthDate.add(years, "year"), "month");
    const days = today.diff(
      birthDate.add(years, "year").add(months, "month"),
      "day",
    );
    return `${years}Y ${months}M ${days}D`;
  };

  // ✅ Cache for pathology reports
  const [pathologyCache, setPathologyCache] = useState({});
  const [totalCount, setTotalCount] = useState(0);

  const fetchAllPathology = async (
    page = 0,
    limit = 10,
    query = "",
    fromDateParam = formattedFromDate,
    toDateParam = formattedToDate,
  ) => {
    try {
      // ================== ✅ DATE RANGE VALIDATION ==================
      if (formattedFromDate && formattedToDate) {
        const from = new Date(formattedFromDate);
        const to = new Date(formattedToDate);

        const diffTime = Math.abs(to - from);
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        if (diffDays > 92) {
          toast.error("You can select maximum 3 months date range");
          setRowLoading(false);
          return;
        }
      }
      setRowLoading(true);
      const endpoint = query
        ? `${BASE_URL}api/user/pathology/search-reports?query=${encodeURIComponent(
            query,
          )}&page=${page}&limit=${limit}&startDate=${fromDateParam}&endDate=${toDateParam}`
        : `${BASE_URL}api/user/pathology/get-pathology-reports?page=${page}&limit=${limit}&startDate=${fromDateParam}&endDate=${toDateParam}`;

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (result.success) {
        const reqData = result.data?.map((path) => ({
          _id: path._id,
          invoice: path?.invoice?.invoiceId,
          doctorName: path?.doctorId?.name,
          patientName: path?.patientId?.name,
          report: path?.result,
          widalReport: path?.widalResult,
          sampleCollectedDate: path?.sampleCollectedDate,
          sampleCollectedTime: path?.sampleCollectedTime,
          widalSampleCollectedDate: path?.widalSampleCollectedDate,
          widalSampleCollectedTime: path?.widalSampleCollectedTime,
          createdAt: path?.createdAt,
          patientId: path?.patientId,
          doctorId: path?.doctorId,
          centreId: path?.invoice?.centerId,
          reportFileUrl: path?.reportFileUrl,
          reportPreviewUrl: path?.reportPreviewUrl,
          widalReportFileUrl: path?.widalReportFileUrl,
          overallNormalStatus: path?.overallNormalStatus,
          overallWidalStatus: path?.overallWidalStatus,
          signatoryDoctor: path?.doctorSignatory,
          techSignatory: path?.techSignatory,
          widalDoctorSignatory: path?.widalDoctorSignatory,
          widalTechSignatory: path?.widalTechSignatory,
        }));

        // ✅ Cache key
        const cacheKey = `${query}_${page}_${limit}_${fromDateParam}_${toDateParam}`;

        // ✅ Store in cache
        setPathologyCache((prev) => ({
          ...prev,
          [cacheKey]: {
            data: reqData,
            totalCount: result.totalCount,
          },
        }));

        setPathologyReport(reqData);
        setTotalCount(result.totalCount || reqData.length);
        setPathologyReportsLoaded(true);
      } else {
        console.error("API failed:", result.message);
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Something went wrong! Please try again.");
    } finally {
      setRowLoading(false);
    }
  };

  const [pdfTemplate, setPdfTemplate] = useState("");
  const fetchPdfTemplate = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}api/user/common/get-pdfTemplate`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();
      setPdfTemplate(data.pdf.orgReportPdfUrl);
    } catch (error) {}
  };

  const [pathoLayout, setPathoLayout] = useState("");
  const fetchPathoLayout = async () => {
    try {
      const response = await fetch(`${BASE_URL}api/user/pathology/layout`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      setPathoLayout(data?.layout);
    } catch (error) {}
  };
  useEffect(() => {
    //fetchAllPathology();
    fetchPdfTemplate();
    fetchPathoLayout();
  }, []);

  useEffect(() => {
    const cacheKey = `${searchQuery}_${page}_${rowsPerPage}_${formattedFromDate}_${formattedToDate}`;

    if (pathologyCache[cacheKey]) {
      // ✅ Load from cache
      setPathologyReport(pathologyCache[cacheKey].data);
      setTotalCount(pathologyCache[cacheKey].totalCount);
    } else {
      // ✅ Fetch from API
      fetchAllPathology(
        page,
        rowsPerPage,
        searchQuery,
        formattedFromDate,
        formattedToDate,
      );
    }
  }, [page, rowsPerPage, searchQuery]);

  const handleReload = () => {
    fetchAllPathology();
  };

  // Pagination Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search Filter
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // reset page
  };

  const paginatedPathologies = pathologyReport;

  const getInvestigationsWithMethods = (reports) => {
    const result = {};
    for (let i = 0; i < reports?.length; i++) {
      reports[i].investigations.forEach((investigation) => {
        const methodWithResult = investigation?.methods.find(
          (method) => method.result !== "",
        );
        if (methodWithResult) {
          result[investigation._id] = methodWithResult._id;
        }
        investigation?.subInvestigations.forEach((subInvestigation) => {
          const subMethodWithResult = subInvestigation.methods.find(
            (method) => method.result !== "",
          );

          if (subMethodWithResult) {
            result[subInvestigation._id] = subMethodWithResult._id;
          }
        });
      });
    }
    return result;
  };

  const handleEdit = (pathology, tab) => {
    // const selectedMethods = getInvestigationsWithMethods(
    //   pathology.report[0].investigations
    // );
    const selectedMethods = getInvestigationsWithMethods(pathology.report);
    navigate("/dashboard/lab/pathology", {
      state: {
        reportId: pathology._id,
        report: pathology.report,
        sentInvoice: {
          patientId: pathology.patientId,
          doctorId: pathology.doctorId,
        },
        sentSelectedMethods: selectedMethods,
        doctorSignatory: pathology.signatoryDoctor,
        techSignatory: pathology.techSignatory,
        reqType: "edit",
        tab: tab,
        widalReport: pathology.widalReport,
        widalDoctorSignatory: pathology.widalDoctorSignatory,
        widalTechSignatory: pathology.widalTechSignatory,
        sampleCollectedDate: pathology.sampleCollectedDate,
        sampleCollectedTime: pathology.sampleCollectedTime,
        widalSampleCollectedDate: pathology.widalSampleCollectedDate,
        widalSampleCollectedTime: pathology.widalSampleCollectedTime,
      },
    });
  };

  const handlePrint = async (pdfUrl) => {
    try {
      setLoading(true);
      const response = await fetch(pdfUrl, { mode: "cors" });
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF. Status: ${response.status}`);
      }
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("pdf")) {
        throw new Error("Fetched file is not a valid PDF.");
      }
      const existingPdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const qrCodeDataUrl = await QRCode.toDataURL(pdfUrl);
      const qrImage = await pdfDoc.embedPng(qrCodeDataUrl);
      const { width, height } = firstPage.getSize();
      firstPage.drawImage(qrImage, {
        x: width - 100, // Adjust position
        y: 700, // Adjust position
        width: 80,
        height: 80,
      });
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      const newTab = window.open(blobUrl, "_blank");
      if (newTab) {
        newTab.focus();
      } else {
        alert("Please allow pop-ups for this website.");
      }
    } catch (error) {
      console.error("Error fetching or processing PDF:", error);
    } finally {
      setLoading(false);
    }
  };

  const getColorTitle = (status) => {
    const result = [];
    if (status === 0) {
      result.push("Pending");
      result.push("warning");
    } else if (status === 1) {
      result.push("Approved");
      result.push("success");
    } else if (status === 2) {
      result.push("Resent");
      result.push("error");
    }
    return result;
  };

  //upload and generate report
  //   const [loading, setLoading] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [boldFont, setBoldFont] = useState(null);
  const [normalFont, setNormalFont] = useState(null);
  const [italicFont, setItalicFont] = useState(null);
  const [barcodeFont, setBarcodeFont] = useState(null);
  const [qrFont, setQrFont] = useState(null);
  function formatDate(dateStr) {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" }); // "Feb"
    const year = date.getFullYear().toString().slice(-2); // "25"
    return `${day}-${month}-${year}`;
  }
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const parts = timeStr.split(":");
    let hours = parts[0];
    let minutes = parts[1];
    const lastPart = parts[2] ? parts[2].toUpperCase() : "";
    const suffix = lastPart.includes("PM") ? "PM" : "AM";
    return `${hours}:${minutes} ${suffix}`;
  };

  function formatDateTime(isoString) {
    const date = new Date(isoString);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" }); // "Feb"
    const year = date.getFullYear().toString().slice(-2); // "25"
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = String(hours % 12 || 12).padStart(2, "0"); // Ensure 2-digit hours
    const formattedMinutes = String(minutes).padStart(2, "0"); // Ensure 2-digit minutes

    return `${day}-${month}-${year} ${formattedHours}:${formattedMinutes} ${ampm}`;
  }
  const addHeader = async (
    pathology,
    page,
    pageNumber,
    normalFont,
    boldFont,
    barcodeFont,
  ) => {
    let yPosition = page.getHeight() - 220;
    let patientInfoX = 25;
    let headerBoxX = 10;
    let lineStartY = 635; // for all 635
    let lineEndY = 720; // for all 720
    let investigationHeaderLineX = 10; // for all 10
    let tableHeaderInvX = 14;
    if (user?._doc?.organizationId?.orgID === "18-1") {
      yPosition = page.getHeight() - 245;
      patientInfoX = 35;
      headerBoxX = 33;
      lineStartY = 610;
      lineEndY = 695;
      investigationHeaderLineX = 30;
      tableHeaderInvX = 33;
    }
    const headerFontSize = 10;
    const labelFontSize = 10;
    const rightXPosition = 300;
    const rightXPosition2 = 380;
    const leftXPosition = 76;
    const pageWidth = page.getWidth();
    const patientFontSize = 11;
    const restFontSize = 9;
    const pageHeight = page.getHeight();
    // Header Background with Rounded Corners
    let Y_POSITION = Number(pageHeight - pathoLayout?.header?.position?.y);
    let X_POSITION_PATIENT_INFO = Number(pathoLayout?.header?.position?.x);
    let X_POSITION_UHID = pathoLayout?.header?.columns?.patient?.width;
    const CONTENT_WIDTH = pathoLayout?.content?.size?.width;
    let CONTENT_Y = Number(pageHeight - pathoLayout?.content?.position?.y);
    let CONTENT_X = Number(pageHeight - pathoLayout?.content?.position?.x);

    page.drawRectangle({
      x: Number(pathoLayout?.header?.position?.x), // for all 10,
      y: Y_POSITION - 95,
      width: Number(pathoLayout?.header?.size?.width), // for all 20,
      height: Number(pathoLayout?.header?.size?.height),
      color: rgb(0.98, 0.98, 0.98), // Soft gray background
      borderColor: rgb(0.8, 0.8, 0.8),
      borderWidth: 0.7,
    });

    yPosition += 90;
    Y_POSITION -= 15;

    page.drawText(`${pathology.patientId.name}`, {
      x: X_POSITION_PATIENT_INFO + 10, // for all 25,
      y: Y_POSITION,
      size: patientFontSize,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    // Invoice ID
    page.drawText("UHID", {
      x: X_POSITION_UHID + 10,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(`: ${pathology.patientId.patientID}`, {
      x: X_POSITION_UHID + 100,
      y: Y_POSITION,
      size: 11,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    yPosition -= 15;
    Y_POSITION -= 15;
    // Gender / Age
    page.drawText("Sex / Age", {
      x: X_POSITION_PATIENT_INFO + 10, //for all 25,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(
      `: ${pathology.patientId.gender} / ${showAgeInReport(
        pathology.patientId.dob,
      )}`,
      {
        x: X_POSITION_PATIENT_INFO + 50,
        y: Y_POSITION,
        size: restFontSize,
        font: normalFont,
        color: rgb(0.2, 0.2, 0.2),
      },
    );

    page.drawText("Case ID", {
      x: X_POSITION_UHID + 10,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(`: ${pathology.invoice}`, {
      x: X_POSITION_UHID + 100,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    // Mobile Number
    yPosition -= 15;
    Y_POSITION -= 15;
    page.drawText("Mobile", {
      x: X_POSITION_PATIENT_INFO + 10, //for all 25,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(`: ${pathology.patientId.mobile}`, {
      x: X_POSITION_PATIENT_INFO + 50,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    page.drawText("Sample Collected", {
      x: X_POSITION_UHID + 10,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(
      `: ${formatDate(pathology.sampleCollectedDate)} ${formatTime(
        pathology.sampleCollectedTime,
      )}`,
      {
        x: X_POSITION_UHID + 100,
        y: Y_POSITION,
        size: restFontSize,
        font: normalFont,
        color: rgb(0.2, 0.2, 0.2),
      },
    );

    // Referred By
    yPosition -= 15;
    Y_POSITION -= 15;
    page.drawText("Ref. By", {
      x: X_POSITION_PATIENT_INFO + 10, //for all 25,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(`: ${pathology.doctorId.name}`, {
      x: X_POSITION_PATIENT_INFO + 50,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    page.drawText("Report Generated", {
      x: X_POSITION_UHID + 10,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(`: ${formatDateTime(pathology.createdAt)}`, {
      x: X_POSITION_UHID + 100,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    // Center
    yPosition -= 15;
    Y_POSITION -= 15;
    page.drawText("Center", {
      x: X_POSITION_PATIENT_INFO + 10, //for all 25,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });

    page.drawLine({
      start: {
        x: Number(pathoLayout?.header?.columns?.patient?.width),
        y: Number(pageHeight - pathoLayout?.header?.position?.y) - 95,
      }, // was 635
      end: {
        x: pathoLayout?.header?.columns?.patient?.width,
        y: Number(pageHeight - pathoLayout?.header?.position?.y),
      }, // was 720
      thickness: 0.7,
      color: rgb(0.8, 0.8, 0.8),
    });

    // page.drawLine({
    //   start: {
    //     x: Number(
    //       pathoLayout?.header?.columns?.patient?.width +
    //         pathoLayout?.header?.columns?.uhid?.width,
    //     ),
    //     y: Number(pageHeight - pathoLayout?.header?.position?.y) - 95,
    //   }, // was 635
    //   end: {
    //     x: Number(
    //       pathoLayout?.header?.columns?.patient?.width +
    //         pathoLayout?.header?.columns?.uhid?.width,
    //     ),
    //     y: Number(pageHeight - pathoLayout?.header?.position?.y),
    //   }, // was 720
    //   thickness: 0.7,
    //   color: rgb(0.8, 0.8, 0.8),
    // });

    page.drawText(`: ${pathology.centreId.centreId}`, {
      x: X_POSITION_PATIENT_INFO + 50,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.2, 0.2, 0.2),
    });
    // page.drawText("*" + pathology.invoice + "*", {
    //   x: rightXPosition,
    //   y: yPosition - 17,
    //   size: 30,
    //   font: barcodeFont,
    // });
    const barcodeCanvas = document.createElement("canvas");
    JsBarcode(barcodeCanvas, pathology.invoice, {
      format: "CODE128",
      width: 2,
      height: 30,
      displayValue: false,
    });
    const barcodeImage = barcodeCanvas.toDataURL("image/png");
    const barcodeBytes = await fetch(barcodeImage).then((res) =>
      res.arrayBuffer(),
    );
    const barcodeImg = await page.doc.embedPng(barcodeBytes);
    page.drawImage(barcodeImg, {
      x: X_POSITION_UHID + 5,
      y: Y_POSITION - 17,
      width: 180,
      height: 30,
    });

    yPosition -= 15;
    Y_POSITION -= 15;
    page.drawText("Lab ID", {
      x: X_POSITION_PATIENT_INFO + 10, //for all 25,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(`: ${localStorage.getItem("orgID").replaceAll("-", ".")}`, {
      x: X_POSITION_PATIENT_INFO + 50,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    // Divider Line

    yPosition -= 17;
    // // Y_POSITION -= 17;
    //   x: Number(pathoLayout?.header?.position?.x), // for all 10,
    //   y: Y_POSITION - 95,
    //   width: Number(pathoLayout?.header?.size?.width),
    page.drawRectangle({
      x: pathoLayout?.content?.position?.x, // for all 10
      y: CONTENT_Y,
      width: pathoLayout?.content?.size?.width, // for all 20
      height: 1,
      color: rgb(0.5, 0.5, 0.5),
    });
    // page.drawLine({
    //   start: { x: Number(pathoLayout?.content?.position?.x), y: CONTENT_Y },
    //   end: { x: Number(pathoLayout?.content?.size?.width), y: CONTENT_Y },
    //   thickness: 0.8,
    //   color: rgb(0.5, 0.5, 0.5),
    // });

    yPosition -= 12;

    // Table Headers
    console.log("CCCXXX", pathoLayout?.content?.position?.x);
    console.log("tableHeaderInvX", tableHeaderInvX);
    page.drawText("INVESTIGATIONS", {
      x: pathoLayout?.content?.position?.x,
      y: CONTENT_Y - 13,
      size: headerFontSize,
      font: boldFont,
    });
    page.drawText("RESULTS", {
      x: pathoLayout?.content?.position?.x + 220,
      y: CONTENT_Y - 13,
      size: headerFontSize,
      font: boldFont,
    });
    page.drawText("UNIT", {
      x: pathoLayout?.content?.position?.x + 340,
      y: CONTENT_Y - 13,
      size: headerFontSize,
      font: boldFont,
    });
    page.drawText("BIO REF. INTERVAL", {
      x: pathoLayout?.content?.position?.x + 420,
      y: CONTENT_Y - 13,
      size: headerFontSize,
      font: boldFont,
    });
    yPosition -= 4;
    // Y_POSITION -= 18;
    CONTENT_Y -= 18;
    page.drawRectangle({
      x: pathoLayout?.content?.position?.x, // for all 10
      y: CONTENT_Y,
      width: pathoLayout?.content?.size?.width, // for all 20
      height: 1,
      color: rgb(0.5, 0.5, 0.5),
    });
    // page.drawLine({
    //   start: { x: pathoLayout?.content?.position?.x, y: CONTENT_Y },
    //   end: { x: CONTENT_WIDTH, y: CONTENT_Y },
    //   thickness: 0.8,
    //   color: rgb(0.5, 0.5, 0.5),
    // });

    // Page Number
    // page.drawText(`Page ${pageNumber}`, {
    //   x: pageWidth - 40,
    //   y: 50,
    //   size: 8,
    //   color: rgb(0, 0, 0),
    // });
  };
  const addWidalHeader1 = async (
    pathology,
    page,
    pageNumber,
    normalFont,
    boldFont,
    barcodeFont,
  ) => {
    let yPosition = page.getHeight() - 220;
    const headerFontSize = 10;
    const labelFontSize = 10;
    const rightXPosition = 300;
    const rightXPosition2 = 380;
    const leftXPosition = 76;
    const pageWidth = page.getWidth();
    const patientFontSize = 11;
    const restFontSize = 9;
    // Header Background with Rounded Corners
    page.drawRectangle({
      x: 10,
      y: yPosition + 10,
      width: pageWidth - 20,
      height: 95,
      color: rgb(0.98, 0.98, 0.98), // Soft gray background
      borderColor: rgb(0.8, 0.8, 0.8),
      borderWidth: 0.7,
    });

    yPosition += 90;

    // Patient Name
    // page.drawText("Patient Name", {
    //   x: 14,
    //   y: yPosition,
    //   size: labelFontSize,
    //   font: normalFont,
    //   color: rgb(0.1, 0.1, 0.1),
    // });
    page.drawText(`${pathology.patientId.name}`, {
      x: 25,
      y: yPosition,
      size: patientFontSize,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    // Invoice ID
    page.drawText("UHID", {
      x: rightXPosition,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(`: ${pathology.patientId.patientID}`, {
      x: rightXPosition2,
      y: yPosition,
      size: 11,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    yPosition -= 15;

    // Gender / Age
    page.drawText("Sex / Age", {
      x: 25,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(
      `: ${pathology.patientId.gender} / ${showAgeInReport(
        pathology.patientId.dob,
      )}`,
      {
        x: leftXPosition,
        y: yPosition,
        size: restFontSize,
        font: normalFont,
        color: rgb(0.2, 0.2, 0.2),
      },
    );

    page.drawText("Case ID", {
      x: rightXPosition,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(`: ${pathology.invoice}`, {
      x: rightXPosition2,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    // Mobile Number
    yPosition -= 15;
    page.drawText("Mobile", {
      x: 25,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(`: ${pathology.patientId.mobile}`, {
      x: leftXPosition,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    page.drawText("Sample Collected", {
      x: rightXPosition,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(
      `: ${formatDate(pathology.widalSampleCollectedDate)} ${
        pathology.widalSampleCollectedTime
      }`,
      {
        x: rightXPosition2,
        y: yPosition,
        size: restFontSize,
        font: normalFont,
        color: rgb(0.2, 0.2, 0.2),
      },
    );

    // Referred By
    yPosition -= 15;
    page.drawText("Ref. By", {
      x: 25,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(`: ${pathology.doctorId.name}`, {
      x: leftXPosition,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    page.drawText("Report Generated", {
      x: rightXPosition,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(`: ${formatDateTime(pathology.createdAt)}`, {
      x: rightXPosition2,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    // Center
    yPosition -= 15;
    page.drawText("Center", {
      x: 25,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });

    //vertical line
    page.drawLine({
      start: { x: 280, y: 635 }, // Bottom point
      end: { x: 280, y: 720 }, // Top point (50px above)
      thickness: 0.7,
      color: rgb(0.8, 0.8, 0.8),
    });
    page.drawLine({
      start: { x: 480, y: 635 }, // Bottom point
      end: { x: 480, y: 720 }, // Top point (50px above)
      thickness: 0.7,
      color: rgb(0.8, 0.8, 0.8),
    });
    page.drawText(`: ${pathology.centreId.centreId}`, {
      x: leftXPosition,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.2, 0.2, 0.2),
    });
    page.drawText("*" + pathology.invoice + "*", {
      x: rightXPosition,
      y: yPosition - 17,
      size: 30,
      font: barcodeFont,
    });

    yPosition -= 15;
    page.drawText("Lab ID", {
      x: 25,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    page.drawText(`: ${localStorage.getItem("orgID").replaceAll("-", ".")}`, {
      x: leftXPosition,
      y: yPosition,
      size: restFontSize,
      font: normalFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    yPosition -= 25; // Move a little lower to create gap after Lab ID
    // const width = pdfDoc.pageWidth();
    page.drawRectangle({
      x: 10,
      y: yPosition - 3,
      width: pageWidth - 20,
      height: 15,
      color: rgb(0.94, 0.94, 0.94),
    });
    const widalText = "WIDAL TEST REPORT";
    const widalFontSize = 14;
    const textWidth = boldFont.widthOfTextAtSize(widalText, widalFontSize);
    const centerX = (pageWidth - textWidth) / 2;
    page.drawText(widalText, {
      x: centerX,
      y: yPosition,
      size: widalFontSize,
      font: boldFont,
      color: rgb(0.502, 0, 0.125),
    });

    // Page Number
    // page.drawText(`Page ${pageNumber}`, {
    //   x: pageWidth - 40,
    //   y: 50,
    //   size: 8,
    //   color: rgb(0, 0, 0),
    // });
  };
  const addWidalHeader = async (
    pathology,
    page,
    pageNumber,
    normalFont,
    boldFont,
    barcodeFont,
  ) => {
    const pageHeight = page.getHeight();
    const pageWidth = page.getWidth();

    const patientFontSize = 11;
    const restFontSize = 9;

    // ===== Layout Driven Positions (same as addHeader) =====
    let Y_POSITION = Number(pageHeight - pathoLayout?.header?.position?.y);
    let X_POSITION_PATIENT_INFO = Number(pathoLayout?.header?.position?.x);
    let X_POSITION_UHID = pathoLayout?.header?.columns?.patient?.width;

    let CONTENT_Y = Number(pageHeight - pathoLayout?.content?.position?.y);
    let CONTENT_X = Number(pathoLayout?.content?.position?.x);

    // ===== Header Background =====
    page.drawRectangle({
      x: X_POSITION_PATIENT_INFO,
      y: Y_POSITION - 95,
      width: Number(pathoLayout?.header?.size?.width),
      height: Number(pathoLayout?.header?.size?.height),
      color: rgb(0.98, 0.98, 0.98),
      borderColor: rgb(0.8, 0.8, 0.8),
      borderWidth: 0.7,
    });

    Y_POSITION -= 15;

    // ===== Patient Name =====
    page.drawText(`${pathology.patientId.name}`, {
      x: X_POSITION_PATIENT_INFO + 10,
      y: Y_POSITION,
      size: patientFontSize,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    // ===== UHID =====
    page.drawText("UHID", {
      x: X_POSITION_UHID + 10,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    page.drawText(`: ${pathology.patientId.patientID}`, {
      x: X_POSITION_UHID + 100,
      y: Y_POSITION,
      size: restFontSize,
      font: boldFont,
    });

    Y_POSITION -= 15;

    // ===== Sex / Age =====
    page.drawText("Sex / Age", {
      x: X_POSITION_PATIENT_INFO + 10,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    page.drawText(
      `: ${pathology.patientId.gender} / ${showAgeInReport(pathology.patientId.dob)}`,
      {
        x: X_POSITION_PATIENT_INFO + 50,
        y: Y_POSITION,
        size: restFontSize,
        font: normalFont,
      },
    );

    // ===== Case ID =====
    page.drawText("Case ID", {
      x: X_POSITION_UHID + 10,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    page.drawText(`: ${pathology.invoice}`, {
      x: X_POSITION_UHID + 100,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    Y_POSITION -= 15;

    // ===== Mobile =====
    page.drawText("Mobile", {
      x: X_POSITION_PATIENT_INFO + 10,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    page.drawText(`: ${pathology.patientId.mobile}`, {
      x: X_POSITION_PATIENT_INFO + 50,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    // ===== Sample Collected (Widal specific) =====
    page.drawText("Sample Collected", {
      x: X_POSITION_UHID + 10,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    page.drawText(
      `: ${formatDate(pathology.widalSampleCollectedDate)} ${pathology.widalSampleCollectedTime}`,
      {
        x: X_POSITION_UHID + 100,
        y: Y_POSITION,
        size: restFontSize,
        font: normalFont,
      },
    );

    Y_POSITION -= 15;

    // ===== Ref By =====
    page.drawText("Ref. By", {
      x: X_POSITION_PATIENT_INFO + 10,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    page.drawText(`: ${pathology.doctorId.name}`, {
      x: X_POSITION_PATIENT_INFO + 50,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    // ===== Report Generated =====
    page.drawText("Report Generated", {
      x: X_POSITION_UHID + 10,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    page.drawText(`: ${formatDateTime(pathology.createdAt)}`, {
      x: X_POSITION_UHID + 100,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    Y_POSITION -= 15;

    // ===== Center =====
    page.drawText("Center", {
      x: X_POSITION_PATIENT_INFO + 10,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    page.drawText(`: ${pathology.centreId.centreId}`, {
      x: X_POSITION_PATIENT_INFO + 50,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    // ===== Vertical Divider (dynamic like addHeader) =====
    page.drawLine({
      start: {
        x: X_POSITION_UHID,
        y: Number(pageHeight - pathoLayout?.header?.position?.y) - 95,
      },
      end: {
        x: X_POSITION_UHID,
        y: Number(pageHeight - pathoLayout?.header?.position?.y),
      },
      thickness: 0.7,
      color: rgb(0.8, 0.8, 0.8),
    });

    // ===== Barcode =====
    const barcodeCanvas = document.createElement("canvas");
    JsBarcode(barcodeCanvas, pathology.invoice, {
      format: "CODE128",
      width: 2,
      height: 30,
      displayValue: false,
    });

    const barcodeImage = barcodeCanvas.toDataURL("image/png");
    const barcodeBytes = await fetch(barcodeImage).then((res) =>
      res.arrayBuffer(),
    );
    const barcodeImg = await page.doc.embedPng(barcodeBytes);

    page.drawImage(barcodeImg, {
      x: X_POSITION_UHID + 5,
      y: Y_POSITION - 20,
      width: 180,
      height: 30,
    });

    // ===== Lab ID =====
    Y_POSITION -= 15;
    page.drawText("Lab ID", {
      x: X_POSITION_PATIENT_INFO + 10,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    page.drawText(`: ${localStorage.getItem("orgID").replaceAll("-", ".")}`, {
      x: X_POSITION_PATIENT_INFO + 50,
      y: Y_POSITION,
      size: restFontSize,
      font: normalFont,
    });

    // ===== Widal Title =====
    Y_POSITION -= 25;
    page.drawRectangle({
      x: CONTENT_X,
      y: Y_POSITION - 3,
      width: pathoLayout?.content?.size?.width,
      height: 15,
      color: rgb(0.94, 0.94, 0.94),
    });

    const widalText = "WIDAL TEST REPORT";
    const widalFontSize = 14;
    const textWidth = boldFont.widthOfTextAtSize(widalText, widalFontSize);
    const centerX = (pageWidth - textWidth) / 2;

    page.drawText(widalText, {
      x: centerX,
      y: Y_POSITION,
      size: widalFontSize,
      font: boldFont,
      color: rgb(0.502, 0, 0.125),
    });
  };

  function extractText(node, font, isUnderlined) {
    let segments = [];
    if (node.nodeType === Node.TEXT_NODE) {
      segments.push({ text: node.nodeValue.trim(), font, isUnderlined });
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      let newFont = font;
      let newUnderline = isUnderlined;

      if (node.tagName === "STRONG") newFont = boldFont;
      if (node.tagName === "U") newUnderline = true;

      node.childNodes.forEach((child) => {
        segments.push(...extractText(child, newFont, newUnderline));
      });
    }
    return segments;
  }

  function groupTestsByDept(data) {
    const groupedByDept = {};
    data.forEach((item) => {
      const dept = item.testId._id.testDept;
      if (!groupedByDept[dept]) {
        groupedByDept[dept] = [];
      }
      groupedByDept[dept].push({
        department: dept,
        testId: item.testId,
        investigations: item.investigations,
      });
    });
    return Object.values(groupedByDept);
  }

  // const calculateAge = (dob) => {
  //   const birthDate = dayjs(dob, "MMMM DD, YYYY");
  //   const today = dayjs();
  //   const diffInDays = today.diff(birthDate, "days");
  //   if (diffInDays < 30) {
  //     return `${diffInDays} days`;
  //   }
  //   let months = today.diff(birthDate, "months");
  //   if (today.date() < birthDate.date()) {
  //     months--;
  //   }
  //   if (months < 12) {
  //     return `${months} months`;
  //   }
  //   let years = Math.floor(months / 12);
  //   return `${years} years`;
  // };
  const calculateAge = (dob) => {
    // Parse correctly using format "MMMM DD, YYYY"
    const birthDate = dayjs(dob, "MMMM DD, YYYY");

    const now = dayjs();
    const years = now.diff(birthDate, "year");
    const months = now.diff(birthDate.add(years, "year"), "month");
    const days = now.diff(
      birthDate.add(years, "year").add(months, "month"),
      "day",
    );
    return `${years}Y ${months}M ${days}D`;
  };

  // function updateInUse(result, pathology) {
  //   const selectedGender = pathology?.patientId?.gender;
  //   const selectedAge = calculateAge(pathology?.patientId?.dob);

  //   // Extract numeric value and unit from selectedAge string
  //   const ageMatch = selectedAge.match(/(\d+)\s*(days|months|years?)/i);
  //   if (!ageMatch) return result; // Return original result if age format is invalid

  //   let parsedAge = parseInt(ageMatch[1], 10);
  //   let ageUnit = ageMatch[2].toLowerCase();

  //   // Convert selectedAge to days for uniform comparison
  //   let ageInDays = parsedAge;
  //   if (ageUnit === "months") ageInDays *= 30; // Approximate conversion
  //   if (ageUnit === "years") ageInDays *= 365;

  //   result.forEach((testGroups) => {
  //     testGroups.forEach((test) => {
  //       test.investigations.forEach((investigation) => {
  //         // Update ranges for methods in investigations
  //         investigation.methods.forEach((method) => {
  //           method.ranges.forEach((range) => {
  //             let fromInDays = parseInt(range.fromValue, 10);
  //             let toInDays = parseInt(range.toValue, 10);

  //             // Convert range values to days
  //             if (range.fromUnit === "months") fromInDays *= 30;
  //             if (range.fromUnit === "years") fromInDays *= 365;
  //             if (range.toUnit === "months") toInDays *= 30;
  //             if (range.toUnit === "years") toInDays *= 365;

  //             // Check if age falls within the range and gender matches
  //             range.inUse =
  //               ageInDays >= fromInDays &&
  //               ageInDays <= toInDays &&
  //               (range.gender === "Both" || range.gender === selectedGender);
  //           });
  //         });
  //         // Process subInvestigations
  //         investigation.subInvestigations.forEach((subInvestigation) => {
  //           subInvestigation.methods.forEach((method) => {
  //             method.ranges.forEach((range) => {
  //               let fromInDays = parseInt(range.fromValue, 10);
  //               let toInDays = parseInt(range.toValue, 10);

  //               // Convert range values to days
  //               if (range.fromUnit === "months") fromInDays *= 30;
  //               if (range.fromUnit === "years") fromInDays *= 365;
  //               if (range.toUnit === "months") toInDays *= 30;
  //               if (range.toUnit === "years") toInDays *= 365;

  //               // Check if age falls within the range and gender matches
  //               range.inUse =
  //                 ageInDays >= fromInDays &&
  //                 ageInDays <= toInDays &&
  //                 (range.gender === "Both" || range.gender === selectedGender);
  //             });
  //           });
  //         });
  //       });
  //     });
  //   });

  //   return result; // Return the modified result
  // }
  const parseAge = (ageStr) => {
    const age = { years: 0, months: 0, days: 0 };
    const matchYear = ageStr.match(/(\d+)\s*Y/i);
    const matchMonth = ageStr.match(/(\d+)\s*M/i);
    const matchDay = ageStr.match(/(\d+)\s*D/i);

    if (matchYear) age.years = parseInt(matchYear[1], 10);
    if (matchMonth) age.months = parseInt(matchMonth[1], 10);
    if (matchDay) age.days = parseInt(matchDay[1], 10);

    return age;
  };
  const ageToDays = (age) => age.years * 365 + age.months * 30 + age.days;
  const compareAges = (a, b) => ageToDays(a) - ageToDays(b);
  // function updateInUse(result, pathology) {
  //   const selectedGender = pathology?.patientId?.gender;
  //   const selectedAgeStr = calculateAge(pathology?.patientId?.dob); // e.g., "17Y 5M 2D"
  //   const age = parseAge(selectedAgeStr); // { years: 17, months: 5, days: 2 }

  //   const evaluateRange = (range) => {
  //     if (!range) return false;

  //     if (range.gender !== selectedGender && range.gender !== "Both")
  //       return false;

  //     const from = { years: 0, months: 0, days: 0 };
  //     const to = { years: 0, months: 0, days: 0 };

  //     const fromVal = parseInt(range.fromValue, 10);
  //     const toVal = parseInt(range.toValue, 10);

  //     if (range.fromUnit === "years") from.years = fromVal;
  //     else if (range.fromUnit === "months") from.months = fromVal;
  //     else if (range.fromUnit === "days") from.days = fromVal;

  //     if (range.toUnit === "years") to.years = toVal;
  //     else if (range.toUnit === "months") to.months = toVal;
  //     else if (range.toUnit === "days") to.days = toVal;

  //     return compareAges(age, from) >= 0 && compareAges(age, to) <= 0;
  //   };

  //   result.forEach((testGroups) => {
  //     testGroups.forEach((test) => {
  //       test.investigations.forEach((investigation) => {
  //         investigation.methods.forEach((method) => {
  //           method.ranges.forEach((range) => {
  //             range.inUse = evaluateRange(range);
  //           });
  //         });

  //         // Sub Investigations
  //         investigation.subInvestigations?.forEach((subInvestigation) => {
  //           subInvestigation.methods.forEach((method) => {
  //             method.ranges.forEach((range) => {
  //               range.inUse = evaluateRange(range);
  //             });
  //           });
  //         });
  //       });
  //     });
  //   });

  //   return result;
  // }

  function updateInUse1(result, pathology) {
    const selectedGender = pathology?.patientId?.gender;
    const selectedAge = calculateAge(pathology?.patientId?.dob); // e.g. "1Y 6M 3D"

    // Parse age parts like "1Y 6M 3D"
    const ageRegex = /(\d+)\s*Y|(\d+)\s*M|(\d+)\s*D/g;
    let years = 0,
      months = 0,
      days = 0;
    let match;

    // Run regex against age string
    while ((match = ageRegex.exec(selectedAge)) !== null) {
      if (match[1]) years = parseInt(match[1], 10);
      if (match[2]) months = parseInt(match[2], 10);
      if (match[3]) days = parseInt(match[3], 10);
    }

    // Convert everything to days
    const totalDays = years * 365 + months * 30 + days;

    if (totalDays === 0) return result; // invalid age format

    const convertToDays = (value, unit) => {
      let days = parseInt(value, 10);
      if (unit === "months") days *= 30;
      if (unit === "years") days *= 365;
      return days;
    };

    result.forEach((testGroups) => {
      testGroups.forEach((test) => {
        test.investigations.forEach((investigation) => {
          const processRanges = (ranges) => {
            ranges.forEach((range) => {
              let fromInDays = convertToDays(range.fromValue, range.fromUnit);
              let toInDays = convertToDays(range.toValue, range.toUnit);
              range.inUse =
                totalDays >= fromInDays &&
                totalDays <= toInDays &&
                (range.gender === "Both" || range.gender === selectedGender);
            });
          };

          // Main methods
          investigation.methods.forEach((method) => {
            processRanges(method.ranges);
          });

          // Sub-investigations
          investigation.subInvestigations.forEach((sub) => {
            sub.methods.forEach((method) => {
              processRanges(method.ranges);
            });
          });
        });
      });
    });

    return result;
  }

  function updateInUse(result, pathology) {
    const selectedGender = pathology?.patientId?.gender;
    const selectedAge = calculateAge(pathology?.patientId?.dob); // e.g. "1Y 6M 3D"

    if (!selectedAge || !selectedGender) return result;

    // Parse age parts like "1Y 6M 3D"
    const ageRegex = /(\d+)\s*Y|(\d+)\s*M|(\d+)\s*D/g;
    let years = 0,
      months = 0,
      days = 0;
    let match;

    while ((match = ageRegex.exec(selectedAge)) !== null) {
      if (match[1]) years = parseInt(match[1], 10);
      if (match[2]) months = parseInt(match[2], 10);
      if (match[3]) days = parseInt(match[3], 10);
    }

    // Convert everything to days
    const totalDays = years * 365 + months * 30 + days;
    if (!totalDays) return result;

    const convertToDays = (value, unit) => {
      let d = parseInt(value, 10);
      if (unit === "months") d *= 30;
      if (unit === "years") d *= 365;
      return d;
    };

    // 🔁 SAFE RANGE PROCESSOR (NO MUTATION)
    const processRanges = (ranges = []) => {
      return ranges.map((range) => {
        const fromInDays = convertToDays(range.fromValue, range.fromUnit);
        const toInDays = convertToDays(range.toValue, range.toUnit);

        return {
          ...range,
          inUse:
            totalDays >= fromInDays &&
            totalDays <= toInDays &&
            (range.gender === "Both" || range.gender === selectedGender),
        };
      });
    };

    // 🔁 FULL IMMUTABLE TRANSFORMATION
    const updatedResult = result.map((testGroups) =>
      testGroups.map((test) => ({
        ...test,
        investigations: test.investigations.map((investigation) => ({
          ...investigation,

          methods: investigation.methods.map((method) => ({
            ...method,
            ranges: processRanges(method.ranges),
          })),

          subInvestigations: investigation.subInvestigations.map((sub) => ({
            ...sub,
            methods: sub.methods.map((method) => ({
              ...method,
              ranges: processRanges(method.ranges),
            })),
          })),
        })),
      })),
    );

    return updatedResult;
  }

  const removeOutsideTests = (data) => {
    return data.map((group) => {
      return group
        .map((test) => ({
          ...test,

          investigations: test.investigations
            .map((investigation) => ({
              ...investigation,

              // 🔥 Remove outside investigations
              methods: investigation.methods,

              subInvestigations: investigation.subInvestigations
                .map((sub) => ({
                  ...sub,
                  methods: sub.methods,
                }))
                .filter(
                  (sub) => sub.methods?.length > 0 && !sub?.testId?.isOutside,
                ),
            }))
            .filter(
              (inv) =>
                (inv.methods?.length > 0 ||
                  inv.subInvestigations?.length > 0) &&
                !inv?.testId?.isOutside, // 🔥 KEY LINE
            ),
        }))
        .filter((test) => !test?.testId?.isOutside); // 🔥 remove top-level test
    });
  };

  function removeEmptyResults(data) {
    return data.map((group) => {
      return group.map((test) => {
        return {
          ...test,
          investigations: test.investigations
            .map((investigation) => {
              return {
                ...investigation,
                methods: investigation.methods.filter(
                  (method) => method.result !== "",
                ),
                subInvestigations: investigation.subInvestigations
                  .map((subInvestigation) => {
                    return {
                      ...subInvestigation,
                      methods: subInvestigation.methods.filter(
                        (method) => method.result !== "",
                      ),
                    };
                  })
                  .filter(
                    (subInvestigation) => subInvestigation.methods?.length > 0,
                  ), // Remove subInvestigations with empty methods
              };
            })
            .filter(
              (investigation) =>
                investigation.methods?.length > 0 ||
                investigation.subInvestigations?.length > 0,
            ), // Remove investigations with empty methods and subInvestigations
        };
      });
    });
  }

  const handleGeneratePDF = async (
    pathology,
    template = 1,
    flag = 1,
    methodPrint,
    save = true,
  ) => {
    // setLoadingStates((prev) => ({ ...prev, [pathology._id]: true }));
    setLoadingStates((prev) => ({
      ...prev,
      [`${pathology._id}_normal`]: true,
    }));
    let pdfUrl;
    if (template === 0) {
      pdfUrl = blankA4;
    } else {
      pdfUrl = `${pdfTemplate}`;
    }
    try {
      const templatePdfBytes = await fetch(pdfUrl).then((res) =>
        res.arrayBuffer(),
      );
      const templatePdf = await PDFDocument.load(templatePdfBytes);
      const pdfDoc = await PDFDocument.create();
      pdfDoc.registerFontkit(fontkit);
      // const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      // const normalFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      // const italicFont = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

      pdfDoc.registerFontkit(fontkit);

      // load unicode fonts
      const normalFontBytes = await fetch(NotoSansRegular).then((res) =>
        res.arrayBuffer(),
      );
      const boldFontBytes = await fetch(NotoSansBold).then((res) =>
        res.arrayBuffer(),
      );

      const normalFont = await pdfDoc.embedFont(normalFontBytes);
      const boldFont = await pdfDoc.embedFont(boldFontBytes);

      // optional italic
      const italicFont = normalFont;

      const fontBytes = await fetch(BarcodeFont).then((res) =>
        res.arrayBuffer(),
      );
      const barcodeFont = await pdfDoc.embedFont(fontBytes);
      const margin = 20;
      const maxWidth = 520;
      const addTemplatePage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templatePdf, [0]);
        return pdfDoc.addPage(templatePage);
      };

      let firstPage = null;
      let yPosition;
      let newPage;
      let width;
      // const pageBreakThreshold = 100;
      // const results = groupTestsByDept(pathology.report);
      // const inUseResult = updateInUse(results, pathology);
      // const finalResult = removeEmptyResults(inUseResult);
      const getPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templatePdf, [0]);
        console.log("templatePage******", templatePage?.getHeight());
        return templatePage;
      };
      let PAGE = await getPage();
      const PAGE_HEIGHT = PAGE.getHeight();
      const pageBreakThreshold =
        PAGE_HEIGHT -
        (pathoLayout.content.position.y + pathoLayout.content.size.height);
      let initialResult =
        flag === 1 ? groupTestsByDept(pathology.report) : [pathology.report]; // wrap in array to keep the structure consistent

      // const inUseResult = updateInUse(initialResult, pathology);
      // const finalResult = removeEmptyResults(inUseResult);
      const inUseResult = updateInUse(initialResult, pathology);
      const noOutside = removeOutsideTests(inUseResult);
      const finalResult = removeEmptyResults(noOutside);

      for (let i = 0; i < finalResult?.length; i++) {
        if (finalResult[i]?.length > 0) {
          const firstDepartment = finalResult[i][0].department;
          //   let newPage;
          if (i === 0) {
            firstPage = await addTemplatePage();
            newPage = firstPage;
          } else {
            newPage = await addTemplatePage();
          }
          addHeader(
            pathology,
            newPage,
            i + 1,
            normalFont,
            boldFont,
            barcodeFont,
          );
          //   yPosition = 542;
          if (flag)
            yPosition =
              newPage?.getHeight() - pathoLayout?.content?.position?.y - 30;
          //user?._doc?.organizationId?.orgID === "18-1" ? 568 : 592;
          //for all 592
          else
            yPosition =
              newPage?.getHeight() - pathoLayout?.content?.position?.y - 20;
          // user?._doc?.organizationId?.orgID === "18-1" ? 578 : 605; // for all 605

          const pageWidth = newPage.getWidth(); // Get the page width
          const deptTextWidth = normalFont.widthOfTextAtSize(
            firstDepartment || "",
            10,
          );
          newPage.drawText(`${firstDepartment || ""}`, {
            x: (pageWidth - deptTextWidth) / 2,
            y: yPosition,
            size: 10,
            font: normalFont,
          });
          yPosition -= 15;
          for (const [testIndex, test] of finalResult[i].entries()) {
            if (test?.investigations?.length === 0) continue;

            width = newPage.getSize().width;
            if (testIndex > 0 && testIndex < finalResult[i].length) {
              yPosition -= 15;
              // start: { x: pathoLayout?.content?.position?.x, y: CONTENT_Y },
              // end: { x: CONTENT_WIDTH, y: CONTENT_Y }
              newPage.drawLine({
                // Abhi
                // start: { x: 20, y: yPosition + 18 },
                // end: { x: width - 20, y: yPosition + 18 },
                start: {
                  x: pathoLayout?.content?.position?.x,
                  y: yPosition + 18,
                },
                end: {
                  x: pathoLayout?.content?.size?.width + 30,
                  y: yPosition + 18,
                },
                thickness: 1,
                color: rgb(0.6, 0.6, 0.6),
              });
            }
            newPage.drawRectangle({
              x: pathoLayout?.content?.position?.x, // for all 10
              y: yPosition - 3,
              width: pathoLayout?.content?.size?.width, // for all 20
              height: 15,
              color: rgb(0.94, 0.94, 0.94),
            });
            const testName = test.testId.testName;
            const testNameWidth = boldFont.widthOfTextAtSize(testName, 10);
            newPage.drawText(`${testName}`, {
              x: (pageWidth - testNameWidth) / 2,
              y: yPosition,
              size: 11,
              font: boldFont,
              color: rgb(0.502, 0, 0.125),
            });
            yPosition -= 15;
            // for (const investigation of test.investigations) {
            for (const [
              index,
              investigation,
            ] of test.investigations.entries()) {
              if (yPosition < pageBreakThreshold) {
                newPage = await addTemplatePage();
                addHeader(
                  pathology,
                  newPage,
                  i + 1,
                  normalFont,
                  boldFont,
                  barcodeFont,
                );
                // yPosition = 530;
                yPosition =
                  newPage?.getHeight() -
                  pathoLayout?.content?.position?.y -
                  (flag === 1 ? 30 : 30);
                //user?._doc?.organizationId?.orgID === "18-1" ? 568 : 592; //592;
              }
              const investigationName = investigation.name;
              const investigationMethodObj = investigation?.methods[0];
              const maxWidth = 200;
              const lineHeight = 12;
              const wrapText = (text, font, fontSize, maxWidth) => {
                const words = text.split(" ");
                let lines = [];
                let currentLine = "";
                words.forEach((word) => {
                  const testLine = currentLine
                    ? `${currentLine} ${word}`
                    : word;
                  const textWidth = normalFont.widthOfTextAtSize(
                    testLine,
                    fontSize,
                  );
                  if (textWidth <= maxWidth) {
                    currentLine = testLine;
                  } else {
                    lines.push(currentLine);
                    currentLine = word;
                  }
                });
                if (currentLine) lines.push(currentLine);
                return lines;
              };
              const fontSize =
                investigation?.subInvestigations?.length > 0 ? 10 : 11;
              const font =
                investigation?.subInvestigations?.length > 0
                  ? boldFont
                  : normalFont;
              const investigationNamelines = wrapText(
                `${investigationName}` || "",
                font,
                fontSize,
                maxWidth,
              );
              investigationNamelines.forEach((line, index) => {
                newPage.drawText(line, {
                  x: pathoLayout?.content?.position?.x, //user?._doc?.organizationId?.orgID === "18-1" ? 33 : margin, // for all margin
                  y: yPosition - index * lineHeight,
                  size: fontSize,
                  font: normalFont,
                });
              });
              const resultMaxWidth = 100;
              const resultLineHeight = 12;
              const resultFont =
                investigationMethodObj?.outOfRange === true
                  ? boldFont
                  : normalFont;
              const resultText = investigationMethodObj?.result
                ? investigationMethodObj.result
                : "";

              const wrapResultText = (text, font, fontSize, maxWidth) => {
                const words = text.split(" ");
                let lines = [];
                let currentLine = "";

                words.forEach((word) => {
                  const testLine = currentLine
                    ? `${currentLine} ${word}`
                    : word;
                  const textWidth = font.widthOfTextAtSize(testLine, fontSize);

                  if (textWidth <= maxWidth) {
                    currentLine = testLine;
                  } else {
                    lines.push(currentLine);
                    currentLine = word;
                  }
                });

                if (currentLine) lines.push(currentLine);
                return lines;
              };

              const resultLines = wrapResultText(
                resultText,
                resultFont,
                11,
                resultMaxWidth,
              );

              // Print wrapped result text
              resultLines.forEach((line, index) => {
                newPage.drawText(line, {
                  x: 250,
                  y: yPosition - 2 - index * resultLineHeight, // Adjust dynamically
                  size: 11,
                  font: resultFont,
                });
              });

              if (investigation?.methods[0]) {
                for (const range of investigation?.methods[0]?.ranges) {
                  if (range?.inUse) {
                    range.unit &&
                      newPage.drawText(range.unit, {
                        x: 370,
                        y: yPosition - 2,
                        size: 11,
                        font: normalFont,
                      });
                  }
                }
              }
              if (investigation?.methods[0]) {
                for (const range of investigation?.methods[0]?.ranges) {
                  if (range?.inUse) {
                    range.unit &&
                      range.min !== null &&
                      range.max !== null &&
                      newPage.drawText(`${range.min} - ${range.max}`, {
                        x: 450,
                        y: yPosition - 2,
                        size: 11,
                        font: normalFont,
                      });
                  }
                }
              }
              const yPositionForNoteNumber = yPosition;
              const maxWrappedLines = Math.max(
                investigationNamelines?.length,
                resultLines?.length,
              );
              const isWrapped = maxWrappedLines > 1;
              let prevYPosition = yPosition;
              if (isWrapped) {
                yPosition -= (maxWrappedLines - 1) * lineHeight;
              }
              if (methodPrint) {
                if (investigation?.methods[0]) {
                  const methodYPosition =
                    prevYPosition - investigationNamelines?.length * lineHeight;
                  newPage.drawText(
                    `[${investigation?.methods[0]?.methodName || ""}]`,
                    {
                      x: pathoLayout?.content?.position?.x + 2, // for all margin
                      y: methodYPosition + 2,
                      size: 6,
                      color: rgb(0.4, 0.4, 0.4),
                      font: normalFont,
                    },
                  );
                  if (investigation?.methods[0]?.methodName) {
                    yPosition -= 9;
                  }
                }
                if (investigation?.methods[0]) {
                  for (const range of investigation?.methods[0]?.ranges) {
                    if (range?.inUse) {
                      if (range.note?.length > 0) {
                        //   yPosition -= 10;
                        newPage.drawText(`[${index + 1}]`, {
                          x: 444,
                          y: yPositionForNoteNumber + 8,
                          size: 7,
                          font: normalFont,
                        });
                      }
                    }
                  }
                }
              }
              yPosition -= 15;
              for (const [
                subIndex,
                subInvestigation,
              ] of investigation?.subInvestigations?.entries()) {
                if (yPosition < pageBreakThreshold) {
                  newPage = await addTemplatePage();
                  addHeader(
                    pathology,
                    newPage,
                    i + 1,
                    normalFont,
                    boldFont,
                    barcodeFont,
                  );
                  //   yPosition = 530; // Reset yPosition for new page
                  yPosition =
                    newPage?.getHeight() -
                    pathoLayout?.content?.position?.y -
                    (flag === 1 ? 30 : 30); //592;
                }
                const subInvestigationName =
                  subInvestigation?.subInvestigationName;
                const subInvestigationMethodObj = subInvestigation?.methods[0];

                const maxWidth = 200;
                const lineHeight = 12;
                const wrapText = (text, font, fontSize, maxWidth) => {
                  const words = text.split(" ");
                  let lines = [];
                  let currentLine = "";
                  words.forEach((word) => {
                    const testLine = currentLine
                      ? `${currentLine} ${word}`
                      : word;
                    const textWidth = normalFont.widthOfTextAtSize(
                      testLine,
                      fontSize,
                    );
                    if (textWidth <= maxWidth) {
                      currentLine = testLine;
                    } else {
                      lines.push(currentLine);
                      currentLine = word;
                    }
                  });
                  if (currentLine) lines.push(currentLine);
                  return lines;
                };
                const subInvestigationNamelines = wrapText(
                  `${subInvestigationName}` || "",
                  normalFont,
                  11,
                  maxWidth,
                );
                subInvestigationNamelines.forEach((line, index) => {
                  newPage.drawText(line, {
                    x: pathoLayout?.content?.position?.x + 5, // for all margin + 5
                    y: yPosition - index * lineHeight,
                    size: 11,
                    font: normalFont,
                  });
                });
                const resultMaxWidth = 100;
                const resultLineHeight = 12;
                const resultFont =
                  subInvestigationMethodObj?.outOfRange === true
                    ? boldFont
                    : normalFont;
                const resultText = subInvestigationMethodObj?.result
                  ? subInvestigationMethodObj.result
                  : "";

                const wrapResultText = (text, font, fontSize, maxWidth) => {
                  const words = text.split(" ");
                  let lines = [];
                  let currentLine = "";

                  words.forEach((word) => {
                    const testLine = currentLine
                      ? `${currentLine} ${word}`
                      : word;
                    const textWidth = font.widthOfTextAtSize(
                      testLine,
                      fontSize,
                    );

                    if (textWidth <= maxWidth) {
                      currentLine = testLine;
                    } else {
                      lines.push(currentLine);
                      currentLine = word;
                    }
                  });

                  if (currentLine) lines.push(currentLine);
                  return lines;
                };

                const resultLines = wrapResultText(
                  resultText,
                  resultFont,
                  11,
                  resultMaxWidth,
                );

                // Print wrapped result text
                resultLines.forEach((line, index) => {
                  newPage.drawText(line, {
                    x: 250,
                    y: yPosition - 2 - index * resultLineHeight, // Adjust dynamically
                    size: 11,
                    font: resultFont,
                  });
                });

                if (subInvestigation?.methods[0]) {
                  for (const range of subInvestigation?.methods[0]?.ranges) {
                    if (range?.inUse) {
                      range.unit &&
                        newPage.drawText(range.unit, {
                          x: 370,
                          y: yPosition - 2,
                          size: 11,
                          font: normalFont,
                        });
                    }
                  }
                }
                if (subInvestigation?.methods[0]) {
                  for (const range of subInvestigation?.methods[0]?.ranges) {
                    if (range?.inUse) {
                      range.unit &&
                        range.min !== null &&
                        range.max !== null &&
                        newPage.drawText(`${range.min} - ${range.max}`, {
                          x: 450,
                          y: yPosition - 2,
                          size: 11,
                          font: normalFont,
                        });
                    }
                  }
                }
                const yPositionForNoteNumber = yPosition;
                const maxWrappedLines = Math.max(
                  subInvestigationNamelines?.length,
                  resultLines?.length,
                );
                const isWrapped = maxWrappedLines > 1;
                let prevYPosition = yPosition;
                if (isWrapped) {
                  yPosition -= (maxWrappedLines - 1) * lineHeight;
                }
                if (methodPrint) {
                  if (subInvestigation?.methods[0]) {
                    //   yPosition -= 10;
                    const methodYPosition =
                      prevYPosition -
                      subInvestigationNamelines?.length * lineHeight;
                    newPage.drawText(
                      "[" + subInvestigation?.methods[0]?.methodName + "]" ||
                        "",
                      {
                        x: pathoLayout?.content?.position?.x + 5, // for all margin + 5
                        y: methodYPosition + 2,
                        size: 6,
                        color: rgb(0.4, 0.4, 0.4),
                        font: normalFont,
                      },
                    );
                    //   yPosition -= 15;
                    if (subInvestigation?.methods[0]?.methodName) {
                      yPosition -= 9;
                    }
                  }
                }
                if (subInvestigation?.methods[0]) {
                  for (const range of subInvestigation?.methods[0]?.ranges) {
                    if (range?.inUse) {
                      if (range.note?.length > 0) {
                        // yPosition -= 10;
                        newPage.drawText(`[${index + 1}.${subIndex + 1}]`, {
                          x: 444,
                          y: yPositionForNoteNumber + 8,
                          size: 7,
                          font: normalFont,
                        });
                        // yPosition -= 10;
                      }
                      //   else {
                      //     yPosition -= 15;
                      //   }
                    }
                  }
                }
                yPosition -= 15;
              }
              //   if (investigation?.subInvestigations?.length > 0) {
              //     yPosition -= 15;
              //   }
            }
            if (yPosition < pageBreakThreshold) {
              newPage = await addTemplatePage();
              addHeader(
                pathology,
                newPage,
                i + 1,
                normalFont,
                boldFont,
                barcodeFont,
              );
              //   yPosition = 530; // Reset yPosition for new page
              yPosition =
                newPage?.getHeight() -
                pathoLayout?.content?.position?.y -
                (flag === 1 ? 30 : 30);
              //user?._doc?.organizationId?.orgID === "18-1" ? 568 : 592; //592;
            }
            const testInterpretation = test?.testId?._id?.interpretation;
            const testNote = test?.testId?.note;

            if (
              typeof testInterpretation === "string" &&
              testInterpretation.trim()
            ) {
              yPosition -= 20;

              const startX = pathoLayout?.content?.position?.x;
              const contentWidth = pathoLayout?.content?.size?.width;

              // ---- Title ----
              newPage.drawText("Interpretation:", {
                x: startX,
                y: yPosition,
                size: 9,
                color: rgb(0.4, 0.4, 0.4),
                font: normalFont,
              });

              yPosition -= 5;

              // ================================
              // 🔥 CREATE HTML → CANVAS
              // ================================
              const tempDiv = document.createElement("div");
              tempDiv.id = "RQ";
              tempDiv.style.position = "absolute";
              tempDiv.style.zIndex = "-999";
              tempDiv.style.top = "-9999px";
              tempDiv.style.left = "-9999px";
              tempDiv.style.width = "1000px";
              tempDiv.style.margin = "0";
              tempDiv.style.padding = "0";
              tempDiv.style.paddingBottom = "20px";
              tempDiv.style.border = "none";
              tempDiv.style.boxSizing = "border-box";
              tempDiv.style.fontFamily = normalFont;

              const styleTag = document.createElement("style");
              const fontSize = "20px";

              styleTag.textContent = `
                #RQ {
                  margin: 0;
                  font-size: ${fontSize} !important;
                  font-family: ${normalFont};
                  white-space: pre-wrap;
                }
                #RQ p, #RQ div {
                  margin-bottom: -5px;
                }
                #RQ table {
                  border-collapse: collapse;
                  width: 100%;
                  margin-top: 10px;
                }
                #RQ table td, #RQ table th {
                  border: 1px solid #000;
                  padding: 0px 6px 15px 6px;
                }
              `;

              tempDiv.appendChild(styleTag);
              appendSanitizedReportHtml(tempDiv, testInterpretation);
              document.body.appendChild(tempDiv);

              const canvas = await html2canvas(tempDiv, {
                backgroundColor: "#ffffff",
                scale: 2,
                useCORS: true,
              });

              document.body.removeChild(tempDiv);

              // ================================
              // 🔥 HARD PAGE GUARD (CRITICAL FIX)
              // ================================
              const MIN_RENDER_HEIGHT = 100;

              if (yPosition - pageBreakThreshold < MIN_RENDER_HEIGHT) {
                newPage = await addTemplatePage();

                addHeader(
                  pathology,
                  newPage,
                  i + 1,
                  normalFont,
                  boldFont,
                  barcodeFont,
                );

                yPosition =
                  newPage.getHeight() -
                  pathoLayout?.content?.position?.y -
                  (flag === 1 ? 30 : 30);
              }

              // ================================
              // 🔥 IMAGE SETUP
              // ================================
              const imgData = canvas.toDataURL("image/png");
              const pngImage = await pdfDoc.embedPng(imgData);
              const pngDims = pngImage.scale(1);

              const originalScale = (newPage.getWidth() - 100) / pngDims.width;
              const targetScale =
                originalScale * (contentWidth / (newPage.getWidth() - 100));

              // ================================
              // 🔥 SAFE HEIGHT CALCULATION
              // ================================
              const safeAvailableHeight = Math.max(
                yPosition - pageBreakThreshold,
                150, // minimum safe height
              );

              const singlePageCanvasHeight = safeAvailableHeight / targetScale;

              let startY = 0;
              let totalHeight = canvas.height;
              let currentPage = newPage;
              let currentY = yPosition;

              // ================================
              // 🔥 FIRST SLICE SAFETY CHECK
              // ================================
              const firstSliceHeight = singlePageCanvasHeight * targetScale;

              if (firstSliceHeight < 80) {
                currentPage = await addTemplatePage();

                addHeader(
                  pathology,
                  currentPage,
                  i + 1,
                  normalFont,
                  boldFont,
                  barcodeFont,
                );

                currentY =
                  currentPage.getHeight() -
                  pathoLayout?.content?.position?.y -
                  (flag === 1 ? 30 : 30);
              }

              // ================================
              // 🔥 MULTI PAGE SPLIT LOGIC
              // ================================
              while (startY < totalHeight) {
                // ================================
                // 🔥 RECALCULATE AVAILABLE HEIGHT PER PAGE
                // ================================
                let pageAvailableHeight = currentY - pageBreakThreshold;

                // if new page → full height available
                if (
                  startY !== 0 &&
                  currentY ===
                    currentPage.getHeight() -
                      pathoLayout?.content?.position?.y -
                      (flag === 1 ? 30 : 30)
                ) {
                  pageAvailableHeight =
                    currentPage.getHeight() -
                    pathoLayout?.content?.position?.y -
                    (flag === 1 ? 30 : 30) -
                    pageBreakThreshold;
                }

                const dynamicCanvasHeight = pageAvailableHeight / targetScale;

                const sliceHeight = Math.min(
                  dynamicCanvasHeight,
                  totalHeight - startY,
                );

                // ================================
                // 🔥 CREATE SLICE
                // ================================
                const splitCanvas = document.createElement("canvas");
                splitCanvas.width = canvas.width;
                splitCanvas.height = sliceHeight;

                const ctx = splitCanvas.getContext("2d");

                ctx.drawImage(
                  canvas,
                  0,
                  startY,
                  canvas.width,
                  sliceHeight,
                  0,
                  0,
                  canvas.width,
                  sliceHeight,
                );

                const splitImgData = splitCanvas.toDataURL("image/png");
                const splitImage = await pdfDoc.embedPng(splitImgData);
                const splitDims = splitImage.scale(targetScale);

                // ================================
                // 🔥 DRAW
                // ================================
                currentPage.drawImage(splitImage, {
                  x: startX,
                  y: currentY - splitDims.height,
                  width: splitDims.width,
                  height: splitDims.height,
                });

                startY += sliceHeight;

                // ================================
                // 🔥 NEW PAGE LOGIC
                // ================================
                if (startY < totalHeight) {
                  currentPage = await addTemplatePage();

                  addHeader(
                    pathology,
                    currentPage,
                    i + 1,
                    normalFont,
                    boldFont,
                    barcodeFont,
                  );

                  currentY =
                    currentPage.getHeight() -
                    pathoLayout?.content?.position?.y -
                    (flag === 1 ? 30 : 30);
                } else {
                  currentY -= splitDims.height;
                  currentY -= 15;
                }
              }

              yPosition = currentY;
              newPage = currentPage;

              yPosition -= 5;
            }

            if (yPosition < pageBreakThreshold) {
              newPage = await addTemplatePage();
              addHeader(
                pathology,
                newPage,
                i + 1,
                normalFont,
                boldFont,
                barcodeFont,
              );
              //   yPosition = 530; // Reset yPosition for new page
              yPosition =
                newPage?.getHeight() -
                pathoLayout?.content?.position?.y -
                (flag === 1 ? 30 : 30);
              //user?._doc?.organizationId?.orgID === "18-1" ? 568 : 592; //592;
            }
            if (testNote?.length > 0) {
              const startX = pathoLayout?.content?.position?.x;
              const contentWidth = pathoLayout?.content?.size?.width;
              const maxX = startX + contentWidth;
              const padding = 5; // optional safe padding

              // 🔹 Title
              newPage.drawText(`Comments:`, {
                x: startX,
                y: yPosition,
                size: 9,
                color: rgb(0.4, 0.4, 0.4),
                font: normalFont,
              });

              yPosition -= 15;

              // 🔹 Parse HTML
              const parser = new DOMParser();
              const doc = parser.parseFromString(testNote, "text/html");

              const paragraphs = [];

              doc.body.querySelectorAll("p").forEach((p) => {
                const segments = extractText(p, boldFont, false);

                const fullText = segments
                  .map((seg) => seg.text)
                  .join(" ")
                  .trim();

                if (fullText) {
                  paragraphs.push({ segments });
                }
              });

              // 🔹 Render paragraphs
              paragraphs.forEach(({ segments }) => {
                let xPosition = startX;

                segments.forEach(({ text, font, isUnderlined }) => {
                  if (!text) return;

                  const words = text.split(" ");

                  words.forEach((word, index) => {
                    const wordWithSpace =
                      index === words.length - 1 ? word : word + " ";

                    const activeFont = font || normalFont;

                    const textWidth = activeFont.widthOfTextAtSize(
                      wordWithSpace,
                      10,
                    );

                    // ✅ PERFECT DYNAMIC WRAP
                    if (xPosition + textWidth > maxX - padding) {
                      yPosition -= 14;
                      xPosition = startX;
                    }

                    // 🔹 Draw text
                    newPage.drawText(wordWithSpace, {
                      x: xPosition,
                      y: yPosition,
                      size: 10,
                      font: normalFont,
                    });

                    // 🔹 Underline support
                    if (isUnderlined) {
                      newPage.drawLine({
                        start: { x: xPosition, y: yPosition - 2 },
                        end: { x: xPosition + textWidth, y: yPosition - 2 },
                        thickness: 1,
                        color: rgb(0, 0, 0),
                      });
                    }

                    xPosition += textWidth;
                  });
                });

                // 🔹 Space between paragraphs
                yPosition -= 16;
              });
            }
            // yPosition -= 20;
            if (yPosition < pageBreakThreshold) {
              newPage = await addTemplatePage();
              addHeader(
                pathology,
                newPage,
                i + 1,
                normalFont,
                boldFont,
                barcodeFont,
              );
              //   yPosition = 530; // Reset yPosition for new page
              yPosition =
                newPage?.getHeight() -
                pathoLayout?.content?.position?.y -
                (flag === 1 ? 30 : 30);
              //user?._doc?.organizationId?.orgID === "18-1" ? 568 : 592; //592;
            }
            for (const [
              index,
              investigation,
            ] of test.investigations.entries()) {
              if (investigation?.methods[0]) {
                for (const range of investigation?.methods[0]?.ranges) {
                  if (range?.inUse) {
                    if (range.note?.length > 0) {
                      if (index === 0) {
                        yPosition -= 15;
                      }
                      yPosition += 5; // Small gap before the note
                      newPage.drawText(`[${index + 1}]`, {
                        x: pathoLayout?.content?.position?.x, //for all margin - 7,
                        y: yPosition + 8,
                        size: 7,
                        font: normalFont,
                      });
                      // Split using newline (\n) instead of multiple spaces
                      const safeNoteLines = range.note
                        //.replace(/[^\x00-\x7F]/g, "") // Remove non-ASCII characters
                        .split(/\n/g);
                      for (const line of safeNoteLines) {
                        newPage.drawText(line.trim(), {
                          x: pathoLayout?.content?.position?.x + 2, //for all margin,
                          y: yPosition,
                          size: 8,
                          font: normalFont,
                        });
                        yPosition -= 8;
                      }

                      yPosition -= 15; // Extra spacing after the note
                      if (yPosition < pageBreakThreshold) {
                        newPage = await addTemplatePage();
                        addHeader(
                          pathology,
                          newPage,
                          i + 1,
                          normalFont,
                          boldFont,
                          barcodeFont,
                        );
                        // yPosition = 530; // Reset yPosition for new page
                        yPosition =
                          newPage?.getHeight() -
                          pathoLayout?.content?.position?.y -
                          (flag === 1 ? 30 : 30);
                      }
                    }
                    // else {
                    //   yPosition -= 10; // Adjust spacing when no note is present
                    // }
                  }
                }
              }
              //   for (const [
              //   index,
              //   subInvestigation,
              // ] of test.investigations.entries()) {}
              for (const [
                subIndex,
                subInvestigation,
              ] of investigation?.subInvestigations?.entries()) {
                if (yPosition < pageBreakThreshold) {
                  newPage = await addTemplatePage();
                  addHeader(
                    pathology,
                    newPage,
                    i + 1,
                    normalFont,
                    boldFont,
                    barcodeFont,
                  );
                  //   yPosition = 530;
                  yPosition = yPosition =
                    newPage?.getHeight() -
                    pathoLayout?.content?.position?.y -
                    (flag === 1 ? 30 : 30); //592; // here
                }
                if (subInvestigation?.methods[0]) {
                  for (const range of subInvestigation?.methods[0]?.ranges) {
                    if (range?.inUse) {
                      if (range.note?.length > 0) {
                        if (subIndex === 0) {
                          yPosition -= 15;
                        }
                        yPosition += 5; // Small gap before the note
                        newPage.drawText(`[${index + 1}.${subIndex + 1}]`, {
                          x: pathoLayout?.content?.position?.x, // for all margin - 7,
                          y: yPosition + 8,
                          size: 7,
                          font: normalFont,
                        });
                        // Split using newline (\n) instead of multiple spaces
                        const noteLines = range.note.split(/\n/g);

                        for (const line of noteLines) {
                          newPage.drawText(line.trim(), {
                            x: pathoLayout?.content?.position?.x + 2, // for all margin,
                            y: yPosition,
                            size: 8,
                            font: normalFont,
                          });
                          yPosition -= 8; // Reduce space between lines
                        }

                        yPosition -= 15; // Extra spacing after the note
                        if (yPosition < pageBreakThreshold) {
                          newPage = await addTemplatePage();
                          addHeader(
                            pathology,
                            newPage,
                            i + 1,
                            normalFont,
                            boldFont,
                            barcodeFont,
                          );
                          //   yPosition = 530; // Reset yPosition for new page
                          yPosition = yPosition =
                            newPage?.getHeight() -
                            pathoLayout?.content?.position?.y -
                            (flag === 1 ? 30 : 30); //592;
                        }
                      }
                      //   else {
                      //     yPosition -= 10; // Adjust spacing when no note is present
                      //   }
                    }
                  }
                }
              }
            }
          }
          // const CONTENT_WIDTH = pathoLayout?.content?.size?.width + 30;
          // let CONTENT_Y = Number(
          //   pageHeight - pathoLayout?.content?.position?.y
          // );
          // let CONTENT_X = Number(
          //   pageHeight - pathoLayout?.content?.position?.x
          // );
          newPage.drawLine({
            start: {
              x: pathoLayout?.content?.position?.x,
              y: yPosition + 10,
            },
            end: {
              x: pathoLayout?.content?.size?.width + 30,
              y: yPosition + 10,
            },
            thickness: 1,
            color: rgb(0.6, 0.6, 0.6),
          });
          const endText = "*** End of report ***";
          const endTextWidth = normalFont.widthOfTextAtSize(endText, 10);
          newPage.drawText(endText, {
            x: (pageWidth - endTextWidth) / 2,
            y: yPosition,
            size: 10,
            font: normalFont,
          });

          //doctor
          /* ================= DOCTOR SIGNATURE (BACKWARD SAFE) ================= */

          // 🔥 get positions (new + fallback old)
          let doctorPositions = [];

          if (pathoLayout?.footer?.doctorSignatures?.positions?.length) {
            doctorPositions = pathoLayout.footer.doctorSignatures.positions;
          } else if (pathoLayout?.footer?.doctorSignature) {
            // fallback → old schema (single doctor)
            doctorPositions = [pathoLayout.footer.doctorSignature];
          }

          // 🔥 get count
          const doctorCount =
            pathoLayout?.footer?.doctorSignatures?.count ||
            doctorPositions.length ||
            1;

          // 🔥 limit doctors based on layout
          const finalDoctors = pathology?.signatoryDoctor?.slice(
            0,
            doctorCount,
          );

          for (let i = 0; i < finalDoctors.length; i++) {
            const doctor = finalDoctors[i]?.doctor;

            // ✅ pick correct position OR fallback to first
            const pos = doctorPositions[i] || doctorPositions[0];

            if (!pos) continue;

            const x = pos.position.x;
            const y = newPage.getHeight() - pos.position.y - 10;

            /* 🔥 SIGNATURE IMAGE */
            if (doctor?.signwebLink) {
              const imageResponse = await fetch(doctor.signwebLink);
              const imageBuffer = await imageResponse.arrayBuffer();
              const contentType = imageResponse.headers.get("content-type");

              let pdfImage;

              if (contentType.includes("png") || contentType.includes("webp")) {
                pdfImage = await pdfDoc.embedPng(imageBuffer);
              } else if (
                contentType.includes("jpeg") ||
                contentType.includes("jpg")
              ) {
                pdfImage = await pdfDoc.embedJpg(imageBuffer);
              } else {
                console.warn("Unsupported image format");
                continue;
              }

              const imgDims = pdfImage.scale(0.5);

              newPage.drawImage(pdfImage, {
                x,
                y,
                width: imgDims.width,
                height: imgDims.height,
              });
            }

            /* 🔥 DOCTOR HEADER TEXT */
            let signHeaderY = newPage.getHeight() - pos.position.y - 20;

            const noteLines = doctor?.signHeader?.split(/\s{2,}/g) || [];

            for (const line of noteLines) {
              newPage.drawText(line.trim(), {
                x,
                y: signHeaderY,
                size: 9,
                font: normalFont,
              });
              signHeaderY -= 10;
            }
          }

          //technician
          // let Width = newPage.getWidth();
          let startSignPosition = margin;
          for (let i = 0; i < pathology?.techSignatory?.length; i++) {
            if (
              pathology?.techSignatory[i]?.technician?.techSignWebLink !== ""
            ) {
              const imageResponse = await fetch(
                pathology?.techSignatory[i]?.technician?.techSignWebLink,
              );
              const imageBuffer = await imageResponse.arrayBuffer();
              const contentType = imageResponse.headers.get("content-type");
              let pdfImage;
              if (contentType.includes("png") || contentType.includes("webp")) {
                pdfImage = await pdfDoc.embedPng(imageBuffer);
              } else if (
                contentType.includes("jpeg") ||
                contentType.includes("jpg")
              ) {
                pdfImage = await pdfDoc.embedJpg(imageBuffer);
              } else {
                console.warn(
                  "Unsupported image format, trying PNG conversion.",
                );
                return;
              }
              const imgDims = pdfImage.scale(0.5);
              newPage.drawImage(pdfImage, {
                x: pathoLayout?.footer?.technicianSignature?.position?.x, // for all startSignPosition,
                y:
                  newPage?.getHeight() -
                  pathoLayout?.footer?.technicianSignature?.position?.y -
                  10,
                width: imgDims.width, //50,
                height: imgDims.height, // 50,
              });
            }
            let techSignHeaderY =
              newPage?.getHeight() -
              pathoLayout?.footer?.technicianSignature?.position?.y -
              20;
            const techNoteLines =
              pathology?.techSignatory[i]?.technician?.technicianHeader.split(
                /\s{2,}/g,
              );
            for (const line of techNoteLines) {
              newPage.drawText(`${line.trim()}`, {
                x: pathoLayout?.footer?.technicianSignature?.position?.x, // for all startSignPosition,
                y: techSignHeaderY,
                // x:
                //   user?._doc?.organizationId?.orgID === "18-1"
                //     ? Width - 125
                //     : startSignPosition,
                // y: techSignHeaderY - 2,
                size: 9,
                font: normalFont,
              });
              techSignHeaderY -= 10;
            }
            startSignPosition += 80;
          }
        }
      }

      const pages = pdfDoc.getPages();

      for (let page of pages) {
        const imgWidth = 50;
        const imgHeight = 50;
        const textSize = 6;
        const text = "This is a verified report";
        const gap = 4;

        // ── QR column X ──────────────────────────────
        const qrBoxLeftX =
          pathoLayout.header.position.x +
          pathoLayout.header.columns.patient.width +
          pathoLayout.header.columns.uhid.width;

        const qrBoxWidth = pathoLayout.header.columns.qr.width;

        // ── Header Y (pdf-lib bottom-left origin) ─────
        const headerTopY = page.getHeight() - pathoLayout.header.position.y;

        const headerHeight = pathoLayout.header.size.height;
        const headerBottomY = headerTopY - headerHeight;

        // ── Block dimensions ─────────────────────────
        const textWidth = normalFont.widthOfTextAtSize(text, textSize);
        const totalBlockHeight = imgHeight + gap + textSize;

        // ── TRUE CENTER of QR box ─────────────────────
        const centerX = qrBoxLeftX + qrBoxWidth / 2;
        const centerY = headerBottomY + headerHeight / 2;

        // ── Block bottom Y (this was the bug) ─────────
        const blockBottomY = centerY - totalBlockHeight / 2;

        // ── Image ────────────────────────────────────
        const imageResponse = await fetch(verifyImg);
        const imageBuffer = await imageResponse.arrayBuffer();
        const Image = await pdfDoc.embedPng(imageBuffer);

        page.drawImage(Image, {
          x: Number(
            pathoLayout?.header?.columns?.patient?.width +
              pathoLayout?.header?.columns?.uhid?.width +
              20,
          ), //qrBoxLeftX, //centerX - imgWidth / 2,
          y: blockBottomY + textSize + gap,
          width: imgWidth,
          height: imgHeight,
        });

        // ── Text ─────────────────────────────────────
        page.drawText(text, {
          x: Number(
            pathoLayout?.header?.columns?.patient?.width +
              pathoLayout?.header?.columns?.uhid?.width +
              10,
          ), //qrBoxLeftX, //centerX - textWidth / 2,
          y: blockBottomY,
          font: normalFont,
          size: textSize,
        });
      }

      const totalPages = pdfDoc.getPageCount();
      pdfDoc.getPages().forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: width - 50,
          y: 38,
          size: 8,
          color: rgb(0, 0, 0), // Black color
        });
      });

      // STOP UPLOAD
      // const pdfBytes1 = await pdfDoc.save();
      // const blob1 = new Blob([pdfBytes1], { type: "application/pdf" });
      // const url = URL.createObjectURL(blob1);
      // window.open(url, "_blank");
      // return;

      // if (template === 0 || !save) {
      //   const qrCodeDataUrl = await QRCode.toDataURL(
      //     pathology.reportPreviewUrl,
      //     { margin: 0 },
      //   );

      //   const qrImageBytes = await fetch(qrCodeDataUrl).then((res) =>
      //     res.arrayBuffer(),
      //   );

      //   const qrImage = await pdfDoc.embedPng(qrImageBytes);

      //   const pages = pdfDoc.getPages();
      //   const qrImgWidth = 71;
      //   const qrImgHeight = 65;

      //   pages.forEach((page) => {
      //     const headerX = pathoLayout.header.position.x;
      //     const headerY = pathoLayout.header.position.y;
      //     const headerHeight = pathoLayout.header.size.height;
      //     const patientW = pathoLayout.header.columns.patient.width;
      //     const uhidW = pathoLayout.header.columns.uhid.width;
      //     const qrW = pathoLayout.header.columns.qr.width;
      //     const qrBoxLeftX = headerX + patientW + uhidW;
      //     const qrBoxWidth = qrW;
      //     const headerTopY = page.getHeight() - headerY;
      //     const headerBottomY = headerTopY - headerHeight;
      //     const gap = 4;
      //     const textSize = 7;
      //     const totalBlockHeight = qrImgHeight + gap + textSize;
      //     const centerX = qrBoxLeftX + qrBoxWidth / 2;
      //     const centerY = headerBottomY + headerHeight / 2;
      //     const blockBottomY = centerY - totalBlockHeight / 2;

      //     page.drawImage(qrImage, {
      //       x: patientW + uhidW + 10,
      //       y: blockBottomY,
      //       width: qrImgWidth,
      //       height: qrImgHeight,
      //     });
      //   });

      //   const pdfBytes = await pdfDoc.save();
      //   const blob = new Blob([pdfBytes], { type: "application/pdf" });
      //   const url = URL.createObjectURL(blob);
      //   window.open(url, "_blank");
      //   return; // ✅ stops here — no upload/save call below
      // }
      if (template === 0 || !save) {
        // Determine QR target based on which storage backend this pathology used
        const isCloudinary = pathology?.reportFileUrl?.includes("cloudinary");

        const qrTargetUrl = isCloudinary
          ? pathology.reportPreviewUrl || pathology.reportFileUrl
          : pathology.reportPreviewUrl; // Drive-based: only trust reportPreviewUrl

        if (qrTargetUrl) {
          const qrCodeDataUrl = await QRCode.toDataURL(qrTargetUrl, {
            margin: 0,
          });

          const qrImageBytes = await fetch(qrCodeDataUrl).then((res) =>
            res.arrayBuffer(),
          );

          const qrImage = await pdfDoc.embedPng(qrImageBytes);

          const pages = pdfDoc.getPages();
          const qrImgWidth = 71;
          const qrImgHeight = 65;

          pages.forEach((page) => {
            const headerX = pathoLayout.header.position.x;
            const headerY = pathoLayout.header.position.y;
            const headerHeight = pathoLayout.header.size.height;
            const patientW = pathoLayout.header.columns.patient.width;
            const uhidW = pathoLayout.header.columns.uhid.width;
            const qrW = pathoLayout.header.columns.qr.width;
            const qrBoxLeftX = headerX + patientW + uhidW;
            const qrBoxWidth = qrW;
            const headerTopY = page.getHeight() - headerY;
            const headerBottomY = headerTopY - headerHeight;
            const gap = 4;
            const textSize = 7;
            const totalBlockHeight = qrImgHeight + gap + textSize;
            const centerX = qrBoxLeftX + qrBoxWidth / 2;
            const centerY = headerBottomY + headerHeight / 2;
            const blockBottomY = centerY - totalBlockHeight / 2;

            page.drawImage(qrImage, {
              x: patientW + uhidW + 10,
              y: blockBottomY,
              width: qrImgWidth,
              height: qrImgHeight,
            });
          });
        } else {
          console.warn(
            `Skipping QR code for pathology ${pathology?._id} — no valid preview URL (file may have been deleted).`,
          );
        }

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        return; // ✅ stops here — no upload/save call below
      }

      // ...existing upload logic stays here, only reached when save === true

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const file = new File([blob], "report.pdf", { type: "application/pdf" });
      const formData = new FormData();
      formData.append("pdfFile", file);
      try {
        const response = await fetch(
          `${BASE_URL}api/user/pathology/upload-pathology-report/${pathology._id}`,
          {
            method: "POST",
            headers: {},
            body: formData,
          },
        );

        const result = await response.json();

        // ✅ HANDLE FAILURE PROPERLY
        if (!response.ok || !result.success) {
          alert(result.message || "Something went wrong");
          return;
        }

        // ✅ SUCCESS
        alert(result.message);

        setPathologyReport((prevPathologies) =>
          prevPathologies.map((p) =>
            p._id === result.data._id
              ? {
                  ...p,
                  reportFileUrl: result.data.reportFileUrl,
                  reportPreviewUrl: result.data.reportPreviewUrl,
                }
              : p,
          ),
        );
      } catch (error) {
        console.error("Error uploading PDF:", error);
        alert("Upload failed. Please try again.");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      //   setLoading(false);
      // setLoadingStates((prev) => ({ ...prev, [pathology._id]: false }));
      setLoadingStates((prev) => ({
        ...prev,
        [`${pathology._id}_normal`]: false,
      }));
    }
  };

  //widalReport
  const handleGenerateWidalPDF1 = async (pathology, template = 1) => {
    // setLoadingStates((prev) => ({ ...prev, [pathology._id]: true }));
    setLoadingStates((prev) => ({
      ...prev,
      [`${pathology._id}_widal`]: true,
    }));
    let pdfUrl = "";
    if (template === 0) {
      pdfUrl = blankA4;
    } else {
      pdfUrl = `${pdfTemplate}`;
    }
    try {
      // Fetch the template
      const templatePdfBytes = await fetch(pdfUrl).then((res) =>
        res.arrayBuffer(),
      );
      const templatePdf = await PDFDocument.load(templatePdfBytes);

      // Create a new PDF and copy the template page
      const pdfDoc = await PDFDocument.create();
      const [templatePage] = await pdfDoc.copyPages(templatePdf, [0]);
      pdfDoc.addPage(templatePage);
      pdfDoc.registerFontkit(fontkit);

      const page = pdfDoc.getPage(0);
      const { width, height } = page.getSize();

      const normalFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const fontBytes = await fetch(BarcodeFont).then((res) =>
        res.arrayBuffer(),
      );
      const barcodeFont = await pdfDoc.embedFont(fontBytes);
      const fontSize = 10;

      const margin = 25;
      const usableWidth = width - margin * 2;
      addWidalHeader(pathology, page, 1, normalFont, boldFont, barcodeFont);

      const headers = [
        "ANTIBODY TITRE",
        "1:20",
        "1:40",
        "1:80",
        "1:160",
        "1:320",
      ];
      const numSmallCols = headers.length - 1;

      const antibodyTitreWidth = usableWidth * 0.4;
      const smallColWidth = (usableWidth - antibodyTitreWidth) / numSmallCols;
      const cellHeight = 25;

      let startX = margin;
      let startY = height - 260; // Adjust Y if template has header (earlier it was 50)

      const getColWidth = (index) =>
        index === 0 ? antibodyTitreWidth : smallColWidth;

      // Draw Header
      let currentX = startX;
      headers.forEach((header, index) => {
        const colWidth = getColWidth(index);

        page.drawRectangle({
          x: currentX,
          y: startY - cellHeight,
          width: colWidth,
          height: cellHeight,
          borderColor: rgb(0, 0, 0),
          borderWidth: 1,
        });

        page.drawText(header, {
          x: currentX + 5,
          y: startY - 18,
          size: fontSize,
          font: boldFont,
          color: rgb(0, 0, 0),
        });

        currentX += colWidth;
      });

      startY -= cellHeight;

      // Draw Data Rows
      pathology.widalReport.data.forEach((item) => {
        const rowData = [
          item.name,
          item["1:20"] || "-",
          item["1:40"] || "-",
          item["1:80"] || "-",
          item["1:160"] || "-",
          item["1:320"] || "-",
        ];

        let currentX = startX;
        rowData.forEach((text, index) => {
          const colWidth = getColWidth(index);

          page.drawRectangle({
            x: currentX,
            y: startY - cellHeight,
            width: colWidth,
            height: cellHeight,
            borderColor: rgb(0, 0, 0),
            borderWidth: 1,
          });

          page.drawText(text, {
            x: currentX + 5,
            y: startY - 18,
            size: fontSize,
            font: index === 0 ? boldFont : normalFont,
            color: rgb(0, 0, 0),
          });

          currentX += colWidth;
        });

        startY -= cellHeight;
      });

      startY -= 20;

      // Draw the "Interpretation" text
      // if (pathology?.widalReport?.testId?.interpretation?.length > 0) {
      //   const interpretationTitle = "Interpretation";
      //   page.drawText(interpretationTitle, {
      //     x: margin,
      //     y: startY,
      //     size: 12,
      //     font: boldFont,
      //   });

      //   // Manually draw an underline
      //   const textWidth = boldFont.widthOfTextAtSize(interpretationTitle, 12);
      //   const underlineY = startY - 2; // slight offset below the text
      //   page.drawLine({
      //     start: { x: margin, y: underlineY },
      //     end: { x: margin + textWidth, y: underlineY },
      //     thickness: 0.75,
      //   });

      //   startY -= 20;
      //   console.log(333, pathology);
      //   // Draw interpretation lines

      //   const interpretationLine =
      //     pathology?.widalReport?.testId?.interpretation
      //       .replace(/<\/p>/gi, "\n")
      //       .replace(/<[^>]+>/g, "")
      //       .split("\n");
      //   for (const line of interpretationLine) {
      //     page.drawText(`${line.trim()}`, {
      //       x: margin,
      //       y: startY,
      //       size: 9,
      //     });
      //     startY -= 10;
      //   }

      //   startY -= 20;
      // }
      if (pathology?.widalReport?.testId?.interpretation?.length > 0) {
        const interpretationTitle = "Interpretation";
        page.drawText(interpretationTitle, {
          x: margin,
          y: startY,
          size: 12,
          font: boldFont,
        });

        const textWidth = boldFont.widthOfTextAtSize(interpretationTitle, 12);
        const underlineY = startY - 2;
        page.drawLine({
          start: { x: margin, y: underlineY },
          end: { x: margin + textWidth, y: underlineY },
          thickness: 0.75,
        });

        startY -= 20;

        const fontSize = 9;
        const lineHeight = fontSize + 3;
        const availableWidth = page.getWidth() - 2 * margin;

        const interpretationText =
          pathology?.widalReport?.testId?.interpretation || "";
        const cleanText = interpretationText
          .replace(/<\/p>/gi, "\n")
          .replace(/<[^>]+>/g, ""); // Strip HTML

        const paragraphs = cleanText.split("\n");

        for (const para of paragraphs) {
          const words = para.trim().split(/\s+/);
          let currentLine = "";

          for (let i = 0; i < words.length; i++) {
            const word = words[i];
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const testWidth = normalFont.widthOfTextAtSize(testLine, fontSize);

            if (testWidth <= availableWidth) {
              currentLine = testLine;
            } else {
              if (
                normalFont.widthOfTextAtSize(word, fontSize) > availableWidth
              ) {
                // Word itself is too long, break it character by character
                for (const char of word) {
                  const testCharLine = currentLine + char;
                  const charLineWidth = normalFont.widthOfTextAtSize(
                    testCharLine,
                    fontSize,
                  );
                  if (charLineWidth <= availableWidth) {
                    currentLine = testCharLine;
                  } else {
                    page.drawText(currentLine, {
                      x: margin,
                      y: startY,
                      size: fontSize,
                      font: normalFont,
                    });
                    startY -= lineHeight;
                    currentLine = char;
                  }
                }
              } else {
                // Word fits, but line is full, print and continue
                page.drawText(currentLine, {
                  x: margin,
                  y: startY,
                  size: fontSize,
                  font: normalFont,
                });
                startY -= lineHeight;
                currentLine = word;
              }
            }
          }

          if (currentLine) {
            page.drawText(currentLine, {
              x: margin,
              y: startY,
              size: fontSize,
              font: normalFont,
            });
            startY -= lineHeight;
          }
        }

        startY -= 20;
      }

      // Draw the "Comment" text
      if (pathology?.widalReport?.comment?.length > 0) {
        const commentTitle = "Comment";
        page.drawText(commentTitle, {
          x: margin,
          y: startY,
          size: 12,
          font: boldFont,
        });

        const textWidthComment = boldFont.widthOfTextAtSize(commentTitle, 12);
        const underlineCommentY = startY - 2;
        page.drawLine({
          start: { x: margin, y: underlineCommentY },
          end: { x: margin + textWidthComment, y: underlineCommentY },
          thickness: 0.75,
        });

        startY -= 20;

        const fontSize = 9;
        const lineHeight = fontSize + 3;
        const availableWidth = page.getWidth() - 2 * margin;

        const commentText = pathology?.widalReport?.comment || "";
        const paragraphs = commentText.split("\n");

        for (const para of paragraphs) {
          const words = para.trim().split(/\s+/);
          let currentLine = "";

          for (let i = 0; i < words.length; i++) {
            const word = words[i];
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const testWidth = normalFont.widthOfTextAtSize(testLine, fontSize);

            if (testWidth <= availableWidth) {
              currentLine = testLine;
            } else {
              // Print currentLine and start a new one
              page.drawText(currentLine, {
                x: margin,
                y: startY,
                size: fontSize,
                font: normalFont,
              });
              startY -= lineHeight;
              currentLine = word; // Start new line with the current word
            }
          }

          // Print the last line of the paragraph
          if (currentLine) {
            page.drawText(currentLine, {
              x: margin,
              y: startY,
              size: fontSize,
              font: normalFont,
            });
            startY -= lineHeight;
          }
        }
      }

      //doctor
      let Width = page.getWidth();
      let startDocSignPosition = margin;
      for (let i = 0; i < pathology?.widalDoctorSignatory?.length; i++) {
        if (pathology.widalDoctorSignatory[i].doctor.signwebLink !== "") {
          const imageResponse = await fetch(
            pathology.widalDoctorSignatory[i].doctor.signwebLink,
          );
          const imageBuffer = await imageResponse.arrayBuffer();
          const contentType = imageResponse.headers.get("content-type");
          let pdfImage;
          if (contentType.includes("png") || contentType.includes("webp")) {
            pdfImage = await pdfDoc.embedPng(imageBuffer);
          } else if (
            contentType.includes("jpeg") ||
            contentType.includes("jpg")
          ) {
            pdfImage = await pdfDoc.embedJpg(imageBuffer);
          } else {
            console.warn("Unsupported image format, trying PNG conversion.");
            return;
          }
          const imgDims = pdfImage.scale(0.5);
          page.drawImage(pdfImage, {
            x: Width - 130,
            y: 80,
            width: imgDims.width, //100,
            height: imgDims.height, //100,
          });
          startDocSignPosition -= 100;
        }
        let signHeaderY = 70;
        const noteLines =
          pathology.widalDoctorSignatory[i].doctor.signHeader.split(/\s{2,}/g);
        for (const line of noteLines) {
          page.drawText(`${line.trim()}`, {
            x: Width - 130,
            y: signHeaderY - 2,
            size: 9,
          });
          signHeaderY -= 10;
        }
        Width -= 100;
      }

      //technician
      // let Width = newPage.getWidth();
      let startSignPosition = margin;
      for (let i = 0; i < pathology?.widalTechSignatory?.length; i++) {
        if (
          pathology?.widalTechSignatory[i]?.technician?.techSignWebLink !== ""
        ) {
          const imageResponse = await fetch(
            pathology?.widalTechSignatory[i]?.technician?.techSignWebLink,
          );
          const imageBuffer = await imageResponse.arrayBuffer();
          const contentType = imageResponse.headers.get("content-type");
          let pdfImage;
          if (contentType.includes("png") || contentType.includes("webp")) {
            pdfImage = await pdfDoc.embedPng(imageBuffer);
          } else if (
            contentType.includes("jpeg") ||
            contentType.includes("jpg")
          ) {
            pdfImage = await pdfDoc.embedJpg(imageBuffer);
          } else {
            console.warn("Unsupported image format, trying PNG conversion.");
            return;
          }
          const imgDims = pdfImage.scale(0.5);
          page.drawImage(pdfImage, {
            x: startSignPosition,
            y: 80,
            width: imgDims.width, //50,
            height: imgDims.height, // 50,
          });
        }
        let techSignHeaderY = 70;
        const techNoteLines =
          pathology?.widalTechSignatory[i]?.technician?.technicianHeader.split(
            /\s{2,}/g,
          );
        for (const line of techNoteLines) {
          page.drawText(`${line.trim()}`, {
            x: startSignPosition,
            y: techSignHeaderY - 2,
            size: 9,
          });
          techSignHeaderY -= 10;
        }
        startSignPosition += 80;
      }

      const pages = pdfDoc.getPages();
      for (let page of pages) {
        page.drawText("This is a verified report", {
          x: 500,
          y: 650, // Adjust position
          font: normalFont,
          size: 7,
        });
        const imageResponse = await fetch(verifyImg);
        const imageBuffer = await imageResponse.arrayBuffer();
        let Image = await pdfDoc.embedPng(imageBuffer);
        // const imgDims = Image.scale(0.5);
        page.drawImage(Image, {
          x: 513,
          y: 660, // Adjust position
          width: 50,
          height: 50,
        });
      }
      const totalPages = pdfDoc.getPageCount();
      pdfDoc.getPages().forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: width - 50,
          y: 38,
          size: 8,
          color: rgb(0, 0, 0), // Black color
        });
      });

      if (template === 0) {
        // Generate QR Code
        const qrCodeDataUrl = await QRCode.toDataURL(
          pathology.widalReportFileUrl,
          {
            margin: 0,
          },
        );
        const qrImageBytes = await fetch(qrCodeDataUrl).then((res) =>
          res.arrayBuffer(),
        );
        const qrImage = await pdfDoc.embedPng(qrImageBytes);

        // Embed QR Code in all pages
        const pages = pdfDoc.getPages();
        pages.forEach((page) => {
          page.drawImage(qrImage, {
            x: 500,
            y: 645, // Adjust Y position (bottom of the page)
            width: 71,
            height: 65,
          });
        });

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        return;
      }

      // Save and open
      // const pdfBytes1 = await pdfDoc.save();
      // const blob1 = new Blob([pdfBytes1], { type: "application/pdf" });
      // const blobUrl1 = URL.createObjectURL(blob1);
      // window.open(blobUrl1, "_blank");
      // return;
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const file = new File([blob], "report.pdf", { type: "application/pdf" });
      const formData = new FormData();
      formData.append("pdfFile", file);
      try {
        const response = await fetch(
          `${BASE_URL}api/user/pathology/upload-widal-pathology-report/${pathology._id}`,
          {
            method: "POST",
            headers: {},
            body: formData,
          },
        );

        if (!response.ok) {
          throw new Error("Failed to upload PDF");
        }
        const result = await response.json();
        if (result.success) {
          alert(result.message);
          setPathologyReport((prevPathologies) =>
            prevPathologies.map((pathology) =>
              pathology._id === result.data._id
                ? {
                    ...pathology,
                    widalReportFileUrl: result.data.widalReportFileUrl,
                  }
                : pathology,
            ),
          );
        }
      } catch (error) {
        console.error("Error uploading PDF:", error);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      //   setLoading(false);
      // setLoadingStates((prev) => ({ ...prev, [pathology._id]: false }));
      setLoadingStates((prev) => ({
        ...prev,
        [`${pathology._id}_widal`]: false,
      }));
    }
  };
  const handleGenerateWidalPDF = async (
    pathology,
    template = 1,
    flag = 1,
    methodPrint,
  ) => {
    setLoadingStates((prev) => ({
      ...prev,
      [`${pathology._id}_widal`]: true,
    }));

    let pdfUrl = template === 0 ? blankA4 : `${pdfTemplate}`;

    try {
      const templatePdfBytes = await fetch(pdfUrl).then((res) =>
        res.arrayBuffer(),
      );
      const templatePdf = await PDFDocument.load(templatePdfBytes);

      const pdfDoc = await PDFDocument.create();
      pdfDoc.registerFontkit(fontkit);

      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const normalFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

      const fontBytes = await fetch(BarcodeFont).then((res) =>
        res.arrayBuffer(),
      );
      const barcodeFont = await pdfDoc.embedFont(fontBytes);

      const addTemplatePage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templatePdf, [0]);
        return pdfDoc.addPage(templatePage);
      };

      let page = await addTemplatePage();

      addWidalHeader(pathology, page, 1, normalFont, boldFont, barcodeFont);

      const PAGE_HEIGHT = page.getHeight();
      let yPosition = PAGE_HEIGHT - pathoLayout?.content?.position?.y - 30;

      const pageBreakThreshold =
        PAGE_HEIGHT -
        (pathoLayout.content.position.y + pathoLayout.content.size.height);

      const margin = pathoLayout?.content?.position?.x;
      const usableWidth = pathoLayout?.content?.size?.width;

      const headers = [
        "ANTIBODY TITRE",
        "1:20",
        "1:40",
        "1:80",
        "1:160",
        "1:320",
      ];

      const antibodyTitreWidth = usableWidth * 0.4;
      const smallColWidth =
        (usableWidth - antibodyTitreWidth) / (headers.length - 1);
      const cellHeight = 25;

      const getColWidth = (index) =>
        index === 0 ? antibodyTitreWidth : smallColWidth;

      // HEADER TABLE
      let currentX = margin;
      headers.forEach((header, index) => {
        const colWidth = getColWidth(index);

        page.drawRectangle({
          x: currentX,
          y: yPosition - cellHeight,
          width: colWidth,
          height: cellHeight,
          borderColor: rgb(0, 0, 0),
          borderWidth: 1,
        });

        page.drawText(header, {
          x: currentX + 5,
          y: yPosition - 18,
          size: 10,
          font: boldFont,
        });

        currentX += colWidth;
      });

      yPosition -= cellHeight;

      // DATA ROWS (dynamic pagination)
      for (const item of pathology.widalReport.data) {
        if (yPosition < pageBreakThreshold) {
          page = await addTemplatePage();
          addWidalHeader(pathology, page, 1, normalFont, boldFont, barcodeFont);
          yPosition = page.getHeight() - pathoLayout?.content?.position?.y - 30;
        }

        const rowData = [
          item.name,
          item["1:20"] || "-",
          item["1:40"] || "-",
          item["1:80"] || "-",
          item["1:160"] || "-",
          item["1:320"] || "-",
        ];

        let currentX = margin;

        rowData.forEach((text, index) => {
          const colWidth = getColWidth(index);

          page.drawRectangle({
            x: currentX,
            y: yPosition - cellHeight,
            width: colWidth,
            height: cellHeight,
            borderColor: rgb(0, 0, 0),
            borderWidth: 1,
          });

          page.drawText(text, {
            x: currentX + 5,
            y: yPosition - 18,
            size: 10,
            font: index === 0 ? boldFont : normalFont,
          });

          currentX += colWidth;
        });

        yPosition -= cellHeight;
      }

      yPosition -= 20;

      // INTERPRETATION (same wrap logic as main PDF)
      if (pathology?.widalReport?.testId?.interpretation?.length > 0) {
        page.drawText("Interpretation:", {
          x: margin,
          y: yPosition,
          size: 10,
          font: boldFont,
        });

        yPosition -= 15;

        const cleanText = pathology.widalReport.testId.interpretation
          .replace(/<\/p>/gi, "\n")
          .replace(/<[^>]+>/g, "");

        const words = cleanText.split(/\s+/);
        let line = "";

        for (const word of words) {
          const testLine = line ? `${line} ${word}` : word;
          const width = normalFont.widthOfTextAtSize(testLine, 10);

          if (width < usableWidth) {
            line = testLine;
          } else {
            page.drawText(line, {
              x: margin,
              y: yPosition,
              size: 10,
              font: normalFont,
            });
            yPosition -= 14;
            line = word;
          }
        }

        if (line) {
          page.drawText(line, {
            x: margin,
            y: yPosition,
            size: 10,
            font: normalFont,
          });
          yPosition -= 14;
        }
      }

      // COMMENT BLOCK (restored – dynamic wrap same as original)
      if (pathology?.widalReport?.comment?.length > 0) {
        const commentTitle = "Comment";
        page.drawText(commentTitle, {
          x: margin,
          y: yPosition,
          size: 12,
          font: boldFont,
        });

        const textWidthComment = boldFont.widthOfTextAtSize(commentTitle, 12);
        const underlineCommentY = yPosition - 2;

        page.drawLine({
          start: { x: margin, y: underlineCommentY },
          end: { x: margin + textWidthComment, y: underlineCommentY },
          thickness: 0.75,
        });

        yPosition -= 20;

        const fontSize = 9;
        const lineHeight = fontSize + 3;
        const availableWidth = page.getWidth() - 2 * margin;

        const commentText = pathology?.widalReport?.comment || "";
        const paragraphs = commentText.split("\n");

        for (const para of paragraphs) {
          const words = para.trim().split(/\s+/);
          let currentLine = "";

          for (let i = 0; i < words.length; i++) {
            const word = words[i];
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const testWidth = normalFont.widthOfTextAtSize(testLine, fontSize);

            if (testWidth <= availableWidth) {
              currentLine = testLine;
            } else {
              page.drawText(currentLine, {
                x: margin,
                y: yPosition,
                size: fontSize,
                font: normalFont,
              });
              yPosition -= lineHeight;
              currentLine = word;
            }
          }

          if (currentLine) {
            page.drawText(currentLine, {
              x: margin,
              y: yPosition,
              size: fontSize,
              font: normalFont,
            });
            yPosition -= lineHeight;
          }
        }

        yPosition -= 20;
      }

      // ===================== GET LAST PAGE =====================
      const pages = pdfDoc.getPages();
      const newPage = pages[pages.length - 1];

      /* ================= DOCTOR SIGNATURE (BACKWARD SAFE) ================= */

      // 🔥 get positions (new + fallback old)
      let doctorPositions = [];

      if (pathoLayout?.footer?.doctorSignatures?.positions?.length) {
        doctorPositions = pathoLayout.footer.doctorSignatures.positions;
      } else if (pathoLayout?.footer?.doctorSignature) {
        // fallback → old schema (single doctor)
        doctorPositions = [pathoLayout.footer.doctorSignature];
      }

      // 🔥 get count
      const doctorCount =
        pathoLayout?.footer?.doctorSignatures?.count ||
        doctorPositions.length ||
        1;

      // 🔥 limit doctors based on layout
      const finalDoctors = pathology?.widalDoctorSignatory?.slice(
        0,
        doctorCount,
      );

      for (let i = 0; i < finalDoctors.length; i++) {
        const doctor = finalDoctors[i]?.doctor;

        // ✅ pick correct position OR fallback to first
        const pos = doctorPositions[i] || doctorPositions[0];

        if (!pos) continue;

        const x = pos.position.x;
        const y = newPage.getHeight() - pos.position.y - 10;

        /* 🔥 SIGNATURE IMAGE */
        if (doctor?.signwebLink) {
          const imageResponse = await fetch(doctor.signwebLink);
          const imageBuffer = await imageResponse.arrayBuffer();
          const contentType = imageResponse.headers.get("content-type");

          let pdfImage;

          if (contentType.includes("png") || contentType.includes("webp")) {
            pdfImage = await pdfDoc.embedPng(imageBuffer);
          } else if (
            contentType.includes("jpeg") ||
            contentType.includes("jpg")
          ) {
            pdfImage = await pdfDoc.embedJpg(imageBuffer);
          } else {
            console.warn("Unsupported image format");
            continue;
          }

          const imgDims = pdfImage.scale(0.5);

          newPage.drawImage(pdfImage, {
            x,
            y,
            width: imgDims.width,
            height: imgDims.height,
          });
        }

        /* 🔥 DOCTOR HEADER TEXT */
        let signHeaderY = newPage.getHeight() - pos.position.y - 20;

        const noteLines = doctor?.signHeader?.split(/\s{2,}/g) || [];

        for (const line of noteLines) {
          newPage.drawText(line.trim(), {
            x,
            y: signHeaderY,
            size: 9,
            font: normalFont,
          });
          signHeaderY -= 10;
        }
      }

      // ===================== TECHNICIAN SIGNATORY =====================
      for (let i = 0; i < pathology?.widalTechSignatory?.length; i++) {
        if (
          pathology?.widalTechSignatory[i]?.technician?.techSignWebLink !== ""
        ) {
          const imageResponse = await fetch(
            pathology?.widalTechSignatory[i]?.technician?.techSignWebLink,
          );

          const imageBuffer = await imageResponse.arrayBuffer();
          const contentType = imageResponse.headers.get("content-type");

          let pdfImage;

          if (contentType.includes("png") || contentType.includes("webp")) {
            pdfImage = await pdfDoc.embedPng(imageBuffer);
          } else if (
            contentType.includes("jpeg") ||
            contentType.includes("jpg")
          ) {
            pdfImage = await pdfDoc.embedJpg(imageBuffer);
          } else {
            console.warn("Unsupported image format");
            continue;
          }

          const imgDims = pdfImage.scale(0.5);

          newPage.drawImage(pdfImage, {
            x: pathoLayout?.footer?.technicianSignature?.position?.x,
            y:
              newPage.getHeight() -
              pathoLayout?.footer?.technicianSignature?.position?.y -
              10,
            width: imgDims.width,
            height: imgDims.height,
          });
        }

        let techSignHeaderY =
          newPage.getHeight() -
          pathoLayout?.footer?.technicianSignature?.position?.y -
          20;

        const techNoteLines =
          pathology?.widalTechSignatory[i]?.technician?.technicianHeader.split(
            /\s{2,}/g,
          );

        for (const line of techNoteLines) {
          newPage.drawText(`${line.trim()}`, {
            x: pathoLayout?.footer?.technicianSignature?.position?.x,
            y: techSignHeaderY,
            size: 9,
            font: normalFont,
          });
          techSignHeaderY -= 10;
        }
      }

      if (template !== 0) {
        // VERIFIED QR — SAME LOGIC AS MAIN PDF
        for (let pg of pdfDoc.getPages()) {
          const imgWidth = 50;
          const imgHeight = 50;
          const textSize = 7;
          const text = "This is a verified report";
          const gap = 4;

          // ── QR Column X ──────────────────────────────
          const qrBoxLeftX =
            pathoLayout.header.position.x +
            pathoLayout.header.columns.patient.width +
            pathoLayout.header.columns.uhid.width;

          const qrBoxWidth = pathoLayout.header.columns.qr.width;

          // ── Header Y (bottom-left origin system) ─────
          const headerTopY = pg.getHeight() - pathoLayout.header.position.y;

          const headerHeight = pathoLayout.header.size.height;
          const headerBottomY = headerTopY - headerHeight;

          // ── Block dimensions ─────────────────────────
          const textWidth = normalFont.widthOfTextAtSize(text, textSize);
          const totalBlockHeight = imgHeight + gap + textSize;

          // ── TRUE CENTER of QR box ─────────────────────
          const centerX = qrBoxLeftX + qrBoxWidth / 2;
          const centerY = headerBottomY + headerHeight / 2;

          // ── Correct bottom Y ─────────────────────────
          const blockBottomY = centerY - totalBlockHeight / 2;

          // ── Load Image ───────────────────────────────
          const imageResponse = await fetch(verifyImg);
          const imageBuffer = await imageResponse.arrayBuffer();
          const Image = await pdfDoc.embedPng(imageBuffer);

          // ── Draw Image (centered properly) ───────────
          page.drawImage(Image, {
            x: Number(
              pathoLayout?.header?.columns?.patient?.width +
                pathoLayout?.header?.columns?.uhid?.width +
                20,
            ), //qrBoxLeftX, //centerX - imgWidth / 2,
            y: blockBottomY + textSize + gap,
            width: imgWidth,
            height: imgHeight,
          });

          // ── Draw Text (centered properly) ────────────
          page.drawText(text, {
            x: Number(
              pathoLayout?.header?.columns?.patient?.width +
                pathoLayout?.header?.columns?.uhid?.width +
                10,
            ), //qrBoxLeftX, //centerX - textWidth / 2,
            y: blockBottomY,
            font: normalFont,
            size: textSize,
          });
        }
      }

      // PAGE NUMBER
      const totalPages = pdfDoc.getPageCount();
      pdfDoc.getPages().forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: page.getWidth() - 50,
          y: 38,
          size: 8,
        });
      });

      // TEMPLATE = QR MODE
      if (template === 0) {
        const qrCodeDataUrl = await QRCode.toDataURL(
          pathology.widalReportFileUrl,
          { margin: 0 },
        );

        const qrBytes = await fetch(qrCodeDataUrl).then((res) =>
          res.arrayBuffer(),
        );

        const qrImage = await pdfDoc.embedPng(qrBytes);

        const qrImgWidth = 71;
        const qrImgHeight = 65;

        pdfDoc.getPages().forEach((pg) => {
          // Header information
          const headerX = pathoLayout.header.position.x;
          const headerY = pathoLayout.header.position.y;
          const headerHeight = pathoLayout.header.size.height;

          const patientW = pathoLayout.header.columns.patient.width;
          const uhidW = pathoLayout.header.columns.uhid.width;
          const qrW = pathoLayout.header.columns.qr.width;

          // Left edge of QR column
          const qrBoxLeftX = headerX + patientW + uhidW;

          // Center horizontally
          const qrX = qrBoxLeftX + (qrW - qrImgWidth) / 2;

          // Center vertically
          const qrY =
            pg.getHeight() - headerY - headerHeight / 2 - qrImgHeight / 2;

          pg.drawImage(qrImage, {
            x: qrX,
            y: qrY,
            width: qrImgWidth,
            height: qrImgHeight,
          });
        });

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        window.open(URL.createObjectURL(blob), "_blank");
        return;
      }

      // Save and open
      // const pdfBytes1 = await pdfDoc.save();
      // const blob1 = new Blob([pdfBytes1], { type: "application/pdf" });
      // const blobUrl1 = URL.createObjectURL(blob1);
      // window.open(blobUrl1, "_blank");
      // return;

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const file = new File([blob], "widal-report.pdf", {
        type: "application/pdf",
      });

      const formData = new FormData();
      formData.append("pdfFile", file);

      await fetch(
        `${BASE_URL}api/user/pathology/upload-widal-pathology-report/${pathology._id}`,
        {
          method: "POST",
          headers: {},
          body: formData,
        },
      );
    } catch (error) {
      console.error("Error generating Widal PDF:", error);
    } finally {
      fetchAllPathology();
      setLoadingStates((prev) => ({
        ...prev,
        [`${pathology._id}_widal`]: false,
      }));
    }
  };

  // const viewReport = (pathology) => {
  //   const url = pathology?.reportFileUrl;
  //   console.log(url, url.includes("cloudinary"));
  //   const isCloudinary = url.includes("cloudinary") ? true : false;
  //   isCloudinary === true
  //     ? viewReportCloudinary(pathology)
  //     : viewReportGoogleDrive(pathology);
  // };
  const viewReport = (pathology) => {
    setWithTemplate(false); // button shows "Print", not "Upload"
    setUploadPathology(pathology);
    setPrintNoTemplate(1); // ✅ use the org template (not blankA4)
    setSaveAfterGenerate(false); // ✅ never save/upload
    setConfirmOpen(true);
  };

  const viewReportCloudinary = async (pathology, type = "normal") => {
    try {
      let fileUrl =
        type === "widal"
          ? pathology.widalReportFileUrl
          : pathology.reportFileUrl;

      setLoadingStates((prev) => ({
        ...prev,
        [`${pathology._id}_${type}`]: true,
      }));

      // Fetch PDF
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Failed to fetch PDF");

      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      // Generate QR
      const qrCodeDataUrl = await QRCode.toDataURL(fileUrl, { margin: 0 });
      const qrImageBytes = await fetch(qrCodeDataUrl).then((res) =>
        res.arrayBuffer(),
      );
      const qrImage = await pdfDoc.embedPng(qrImageBytes);

      const pages = pdfDoc.getPages();

      const qrImgWidth = 71;
      const qrImgHeight = 65;

      pages.forEach((page) => {
        const pageHeight = page.getHeight();

        const headerX = pathoLayout.header.position.x;
        const headerY = pathoLayout.header.position.y;
        const headerHeight = pathoLayout.header.size.height;

        const patientW = pathoLayout.header.columns.patient.width;
        const uhidW = pathoLayout.header.columns.uhid.width;
        const qrW = pathoLayout.header.columns.qr.width;

        // 🔑 QR column left edge
        const qrBoxLeftX = headerX + patientW + uhidW;

        // 🔑 Center horizontally
        const qrX = qrBoxLeftX + (qrW - qrImgWidth) / 2;

        // 🔑 Center vertically in header
        const qrY = pageHeight - headerY - headerHeight / 2 - qrImgHeight / 2;

        // const qrBoxLeftX =
        //   pathoLayout.header.position.x +
        //   pathoLayout.header.columns.patient.width +
        //   pathoLayout.header.columns.uhid.width;

        const qrBoxWidth = pathoLayout.header.columns.qr.width;

        // ── Header Y (pdf-lib bottom-left origin) ─────
        const headerTopY = page.getHeight() - pathoLayout.header.position.y;

        // const headerHeight = pathoLayout.header.size.height;
        const headerBottomY = headerTopY - headerHeight;

        // ── Block dimensions ─────────────────────────
        const gap = 4;
        const textSize = 7;
        // const textWidth = normalFont.widthOfTextAtSize(text, textSize);
        const totalBlockHeight = qrImgHeight + gap + textSize;

        // ── TRUE CENTER of QR box ─────────────────────
        const centerX = qrBoxLeftX + qrBoxWidth / 2;
        const centerY = headerBottomY + headerHeight / 2;

        // ── Block bottom Y (this was the bug) ─────────
        const blockBottomY = centerY - totalBlockHeight / 2;

        page.drawImage(qrImage, {
          x:
            pathoLayout?.header?.columns?.patient?.width +
            pathoLayout?.header?.columns?.uhid?.width +
            10,
          y: blockBottomY,
          width: qrImgWidth,
          height: qrImgHeight,
        });
      });

      // Save & open
      const updatedPdfBytes = await pdfDoc.save();
      const blob = new Blob([updatedPdfBytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      logPrintReport(pathology._id);
      window.open(blobUrl, "_blank");
    } catch (error) {
      console.error("Error fetching or processing PDF:", error);
      alert("Failed to load the report. Please try again.");
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        [`${pathology._id}_${type}`]: false,
      }));
    }
  };

  const viewReportGoogleDrive = async (pathology, type = "normal") => {
    try {
      // 🔥 USE BACKEND PROXY INSTEAD OF GOOGLE URL
      let fileUrl =
        type === "widal"
          ? `${BASE_URL}api/user/pathology/drive/${pathology.widalDriveFileId}`
          : `${BASE_URL}api/user/pathology/drive/${pathology.reportFileUrl}`;

      setLoadingStates((prev) => ({
        ...prev,
        [`${pathology._id}_${type}`]: true,
      }));

      // Fetch PDF (NOW WORKS ✅)
      const response = await fetch(fileUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error("Failed to fetch PDF");

      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      // 🔥 QR SHOULD USE PREVIEW LINK (NOT API URL)
      const qrUrl =
        type === "widal"
          ? pathology.widalReportFileUrl
          : pathology.reportPreviewUrl;
      console.log(111, qrUrl);
      const qrCodeDataUrl = await QRCode.toDataURL(qrUrl, { margin: 0 });

      const qrImageBytes = await fetch(qrCodeDataUrl).then((res) =>
        res.arrayBuffer(),
      );

      const qrImage = await pdfDoc.embedPng(qrImageBytes);

      const pages = pdfDoc.getPages();

      const qrImgWidth = 71;
      const qrImgHeight = 65;

      pages.forEach((page) => {
        const headerX = pathoLayout.header.position.x;
        const headerHeight = pathoLayout.header.size.height;

        const patientW = pathoLayout.header.columns.patient.width;
        const uhidW = pathoLayout.header.columns.uhid.width;
        const qrW = pathoLayout.header.columns.qr.width;

        const qrBoxLeftX = headerX + patientW + uhidW;
        const qrBoxWidth = qrW;

        const headerTopY = page.getHeight() - pathoLayout.header.position.y;
        const headerBottomY = headerTopY - headerHeight;

        const gap = 4;
        const textSize = 7;
        const totalBlockHeight = qrImgHeight + gap + textSize;

        const centerY = headerBottomY + headerHeight / 2;
        const blockBottomY = centerY - totalBlockHeight / 2;

        page.drawImage(qrImage, {
          x:
            pathoLayout.header.columns.patient.width +
            pathoLayout.header.columns.uhid.width +
            10,
          y: blockBottomY,
          width: qrImgWidth,
          height: qrImgHeight,
        });
      });

      // Save & open
      const updatedPdfBytes = await pdfDoc.save();
      const blob = new Blob([updatedPdfBytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      logPrintReport(pathology._id);
      window.open(blobUrl, "_blank");
    } catch (error) {
      console.error("Error fetching or processing PDF:", error);
      alert("Failed to load the report. Please try again.");
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        [`${pathology._id}_${type}`]: false,
      }));
    }
  };

  const viewWidalReport = async (pathology) => {
    try {
      let fileUrl = pathology.widalReportFileUrl;

      setLoadingStates((prev) => ({
        ...prev,
        [`${pathology._id}_widal`]: true,
      }));

      // Fetch PDF
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Failed to fetch PDF");

      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      // Generate QR
      const qrCodeDataUrl = await QRCode.toDataURL(fileUrl, { margin: 0 });
      const qrImageBytes = await fetch(qrCodeDataUrl).then((res) =>
        res.arrayBuffer(),
      );
      const qrImage = await pdfDoc.embedPng(qrImageBytes);

      const qrImgWidth = 71;
      const qrImgHeight = 65;
      const pages = pdfDoc.getPages();

      pages.forEach((page) => {
        const pageHeight = page.getHeight();

        const headerX = pathoLayout.header.position.x;
        const headerY = pathoLayout.header.position.y;
        const headerHeight = pathoLayout.header.size.height;

        const patientW = pathoLayout.header.columns.patient.width;
        const uhidW = pathoLayout.header.columns.uhid.width;
        const qrW = pathoLayout.header.columns.qr.width;

        // 🔑 QR column left edge
        const qrBoxLeftX = headerX + patientW + uhidW;

        // 🔑 Center horizontally
        const qrX = qrBoxLeftX + (qrW - qrImgWidth) / 2;

        // 🔑 Center vertically in header
        const qrY = pageHeight - headerY - headerHeight / 2 - qrImgHeight / 2;

        // const qrBoxLeftX =
        //   pathoLayout.header.position.x +
        //   pathoLayout.header.columns.patient.width +
        //   pathoLayout.header.columns.uhid.width;

        const qrBoxWidth = pathoLayout.header.columns.qr.width;

        // ── Header Y (pdf-lib bottom-left origin) ─────
        const headerTopY = page.getHeight() - pathoLayout.header.position.y;

        // const headerHeight = pathoLayout.header.size.height;
        const headerBottomY = headerTopY - headerHeight;

        // ── Block dimensions ─────────────────────────
        const gap = 4;
        const textSize = 7;
        // const textWidth = normalFont.widthOfTextAtSize(text, textSize);
        const totalBlockHeight = qrImgHeight + gap + textSize;

        // ── TRUE CENTER of QR box ─────────────────────
        const centerX = qrBoxLeftX + qrBoxWidth / 2;
        const centerY = headerBottomY + headerHeight / 2;

        // ── Block bottom Y (this was the bug) ─────────
        const blockBottomY = centerY - totalBlockHeight / 2;

        //           page.drawImage(Image, {
        //     x: Number(
        //       pathoLayout?.header?.columns?.patient?.width +
        //         pathoLayout?.header?.columns?.uhid?.width +
        //         20,
        //     ), //qrBoxLeftX, //centerX - imgWidth / 2,
        //     y: blockBottomY + textSize + gap,
        //     width: imgWidth,
        //     height: imgHeight,
        //   });

        //   // ── Text ─────────────────────────────────────
        //   page.drawText(text, {
        //     x: Number(
        // pathoLayout?.header?.columns?.patient?.width +
        //   pathoLayout?.header?.columns?.uhid?.width +
        //   10,
        //     ), //qrBoxLeftX, //centerX - textWidth / 2,
        //     y: blockBottomY,
        //     font: normalFont,
        //     size: textSize,
        //   });
        // }
        page.drawImage(qrImage, {
          x:
            pathoLayout?.header?.columns?.patient?.width +
            pathoLayout?.header?.columns?.uhid?.width +
            10,
          y: blockBottomY,
          width: qrImgWidth,
          height: qrImgHeight,
        });
      });

      // Save & open
      const updatedPdfBytes = await pdfDoc.save();
      const blob = new Blob([updatedPdfBytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      logPrintReport(pathology._id);
      window.open(blobUrl, "_blank");
    } catch (error) {
      console.error("Error fetching or processing PDF:", error);
      alert("Failed to load the report. Please try again.");
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        [`${pathology._id}_widal`]: false,
      }));
    }
  };

  const logPrintReport = async (id) => {
    try {
      const response = await fetch(`${BASE_URL}api/user/common/log-print`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id, print: "pathologyReport" }),
      });
    } catch (error) {
      console.log(error);
    }
  };
  const formatDateToReadableString = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "long" });
    const year = date.getFullYear();
    const formattedDay = day < 10 ? `0${day}` : day;
    return `${month} ${formattedDay}, ${year}`;
  };
  const getFormattedDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // Extracts YYYY-MM-DD from ISO format
  };
  // const [fromDate, setFromDate] = useState(getFormattedDate());
  // const [toDate, setToDate] = useState(getFormattedDate());
  // const [formattedFromDate, setFormattedFromDate] = useState(
  //   getFormattedCurrentDate()
  // );
  // const [formattedToDate, setFormattedToDate] = useState(
  //   getFormattedCurrentDate()
  // );
  const [openLogDialog, setOpenLogDialog] = useState(false);
  const [invoiceLogs, setInvoiceLogs] = useState(null);
  const handleViewLog = async (id) => {
    setOpenLogDialog(true);
    try {
      const response = await fetch(
        `${BASE_URL}api/user/common/get-logs?id=${id}&targetType=${TARGET_TYPE.pathologyReport}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      const invoice = await response.json();
      setInvoiceLogs(invoice.logs);
    } catch (error) {
      console.error("Error:", error);
      alert("Something went wrong! Please try again.");
    } finally {
      // setLoading(false);
    }
  };
  const handleCloseLog = () => {
    setOpenLogDialog(false);
  };
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [printNoTemplate, setPrintNoTemplate] = useState(1);
  const [withTemplate, setWithTemplate] = useState(true);
  const [saveAfterGenerate, setSaveAfterGenerate] = useState(true);
  const printWithoutTemplate = (pathology) => {
    setWithTemplate(false);
    setUploadPathology(pathology);
    setPrintNoTemplate(0);
    setSaveAfterGenerate(false); // ✅ never save
    setConfirmOpen(true);
  };
  let groupByDept = 0; // 0 or 1
  let printWithMethod = false;
  return (
    <Box
      sx={{
        mt: 1,
      }}
    >
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0px 8px 30px rgba(0,0,0,0.2)",
            overflow: "hidden",
            bgcolor: "#f9fafb",
          },
        }}
      >
        {/* Title with gradient */}
        <DialogTitle>Print Options</DialogTitle>

        {/* Content */}
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom color="text.secondary">
            Customize your report before printing:
          </Typography>

          <Stack spacing={2} mt={2}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1.5,
                borderRadius: 2,
                bgcolor: "white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                transition: "all 0.2s",
                "&:hover": { bgcolor: "#f3f4f6" },
              }}
            >
              <Layers size={20} color="#2563eb" style={{ marginRight: 10 }} />
              <FormControlLabel
                control={
                  <Switch
                    // defaultChecked
                    onChange={(e) => (groupByDept = e.target.checked ? 1 : 0)}
                    color="success"
                  />
                }
                label="Group Tests by Department"
                sx={{
                  flex: 1,
                  ".MuiFormControlLabel-label": { fontWeight: 500 },
                }}
              />
              <Tooltip
                title="Enable / Disable this option to group test results by department in the PDF."
                placement="top"
                arrow
              >
                <IconButton size="small" color="primary">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1.5,
                borderRadius: 2,
                bgcolor: "white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                transition: "all 0.2s",
                "&:hover": { bgcolor: "#f3f4f6" },
              }}
            >
              <FileText size={20} color="#2563eb" style={{ marginRight: 10 }} />
              <FormControlLabel
                control={
                  <Switch
                    // defaultChecked
                    onChange={(e) => (printWithMethod = e.target.checked)}
                    color="success"
                  />
                }
                label="Print with Methods"
                sx={{
                  flex: 1,
                  ".MuiFormControlLabel-label": { fontWeight: 500 },
                }}
              />
              <Tooltip
                title="Enable / Disable this option to include test methods in the printout."
                placement="top"
                arrow
              >
                <IconButton size="small" color="primary">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            p: 2,
            justifyContent: "space-between",
            bgcolor: "#f3f4f6",
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <Button
            onClick={() => setConfirmOpen(false)}
            color="error"
            variant="outlined"
            startIcon={<XCircle />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: "bold",
              "&:hover": { bgcolor: "#fee2e2" },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => {
              handleGeneratePDF(
                uploadPathology,
                printNoTemplate,
                groupByDept ?? 1,
                printWithMethod ?? true,
                saveAfterGenerate,
              );
              setConfirmOpen(false);
            }}
            color={withTemplate ? "primary" : "success"}
            variant="contained"
            startIcon={withTemplate ? <Upload /> : <PrintIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: "bold",
              px: 3,
            }}
          >
            {withTemplate ? "Upload" : "Print"}
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer
        component={Paper}
        elevation={3}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginTop: 2,
              marginLeft: 5,
            }}
          >
            <Typography>From: </Typography>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value); // Store in YYYY-MM-DD for input
                setFormattedFromDate(
                  formatDateToReadableString(e.target.value),
                ); // Store in readable format
              }}
              style={{
                // width: "100%",
                padding: "6px 8px",
                fontSize: "14px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                outline: "none",
                transition: "all 0.3s ease-in-out",
                boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.1)",
                marginRight: 5,
              }}
            />
            <Typography>To: </Typography>
            <input
              type="date"
              value={toDate} // Bind with the correct format for the input
              onChange={(e) => {
                setToDate(e.target.value); // Store in YYYY-MM-DD for input
                setFormattedToDate(formatDateToReadableString(e.target.value)); // Store in readable format
              }}
              style={{
                // width: "100%",
                padding: "6px 8px",
                fontSize: "14px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                outline: "none",
                transition: "all 0.3s ease-in-out",
                boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.1)",
              }}
            />
            {fromDate && toDate && (
              <ContentPasteSearchIcon
                onClick={fetchAllPathology}
                sx={{ fontSize: "2rem", color: "#ef2d56", cursor: "pointer" }}
              />
            )}
          </div>
          {paginatedPathologies && (
            <TablePagination
              component="div"
              count={totalCount} // ✅ important (server count)
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1,
          }}
        >
          <Chip
            avatar={
              <Avatar
                sx={{
                  backgroundColor: "#d0f0c0",
                  color: "#000",
                }}
              >
                {totalCount}
              </Avatar>
            }
            label="pathology"
            color="success"
          />
          <TextField
            placeholder="Search pathology"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearch}
            sx={{
              width: "20%",
              ml: "auto",
            }}
          />
          <button
            onClick={handleReload}
            style={{
              display: "flex",
              alignItems: "center",
              border: "none",
              background: "none",
              cursor: "pointer",
              padding: "8px",
            }}
          >
            <RefreshIcon style={{ color: "#1976d2", fontSize: "24px" }} />
          </button>
        </Box>

        <Table>
          {/* Table Head */}
          <TableHead
            sx={{
              backgroundColor: "#d9e2ff",
              "& .MuiTableCell-head": {
                fontWeight: "bold",
                fontSize: "14px",
                color: "#5c6bc0",
                textTransform: "uppercase",
              },
            }}
          >
            <TableRow>
              <TableCell>Invoice ID</TableCell>
              <TableCell>Patient Name</TableCell>
              <TableCell>Doctor Name</TableCell>
              <TableCell>Actions</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
            {rowLoading && (
              <TableRow>
                <TableCell colSpan={5} sx={{ padding: 0 }}>
                  <LinearProgress
                    color="success"
                    sx={{ height: 2, width: "100%" }}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {paginatedPathologies ? (
              paginatedPathologies?.map((pathology, index) => (
                <TableRow
                  key={index}
                  sx={{
                    position: "relative",
                    "&:hover": {
                      backgroundColor: "#f1f8ff",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    },
                    transition:
                      "background-color 0.2s ease, box-shadow 0.2s ease",
                    "& .invoiceId": {
                      transition: "transform 0.2s ease, opacity 0.2s ease",
                    },
                    "&:hover .invoiceId": {
                      transform: "translateY(-5px)",
                    },
                    "&:hover .viewLogBtn": {
                      opacity: 1,
                      visibility: "visible",
                    },
                  }}
                >
                  <TableCell>
                    <Box className="invoiceId">{pathology.invoice}</Box>
                    {user?._doc?.role === "Admin" && (
                      <Typography
                        className="viewLogBtn"
                        color="warning"
                        onClick={() => handleViewLog(pathology._id)}
                        sx={{
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          position: "absolute",
                          top: "54%",
                          left: "15px",
                          mt: "2px",
                          opacity: 0,
                          visibility: "hidden",
                          transition: "opacity 0.2s ease, visibility 0.2s ease",
                        }}
                      >
                        <FormatAlignLeftIcon sx={{ fontSize: "1rem" }} />
                        view logs
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{pathology.patientName}</TableCell>
                  <TableCell>{pathology.doctorName}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {user?._doc?.permissions?.pathologyManagement?.edit &&
                          pathology?.report?.length > 0 && (
                            <Tooltip
                              title="Edit Normal Report"
                              arrow
                              placement="top"
                            >
                              <IconButton size="small">
                                <EditIcon
                                  color="primary"
                                  sx={{ cursor: "pointer" }}
                                  onClick={() =>
                                    handleEdit(pathology, "normal")
                                  }
                                />
                              </IconButton>
                            </Tooltip>
                          )}
                        {/* {user?._doc?.permissions?.pathologyManagement?.delete &&
                          pathology?.report?.length > 0 && (
                            <Tooltip
                              title="Delete Normal Report"
                              arrow
                              placement="top"
                            >
                              <IconButton size="small">
                                <Delete
                                  color="error"
                                  sx={{ cursor: "pointer" }}
                                  onClick={() =>
                                    handleEdit(pathology, "normal")
                                  }
                                />
                              </IconButton>
                            </Tooltip>
                          )} */}
                        {/* {pathology.overallStatus === 1 && */}
                        {pathology?.report?.length > 0 &&
                          (pathology.reportFileUrl?.length === 0 ? (
                            pathology.overallNormalStatus === 1 ? (
                              !loadingStates[`${pathology._id}_normal`] ? (
                                <Tooltip
                                  title="Upload Normal Report"
                                  arrow
                                  placement="top"
                                >
                                  <IconButton size="small">
                                    <UploadFileIcon
                                      color="success"
                                      sx={{ cursor: "pointer" }}
                                      //   onClick={() => handlePrint(pathology.reportFileUrl)}
                                      onClick={() => {
                                        //handleGeneratePDF(pathology)
                                        setSaveAfterGenerate(true);
                                        setWithTemplate(true);
                                        setUploadPathology(pathology);
                                        setConfirmOpen(true);
                                      }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <IconButton size="small">
                                  <CircularProgress color="success" size={24} />
                                </IconButton>
                              )
                            ) : (
                              ""
                            )
                          ) : !loadingStates[`${pathology._id}_normal`] ? (
                            <>
                              <Tooltip
                                title="Print with template"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => viewReport(pathology)}
                                >
                                  <NoteAddIcon
                                    sx={{ color: "#2a9d8f" }}
                                    size={24}
                                  />
                                </IconButton>
                              </Tooltip>

                              <Tooltip
                                title="Print without template"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    // handleGeneratePDF(pathology, 0)
                                    printWithoutTemplate(pathology)
                                  }
                                >
                                  <ContentCopyIcon
                                    sx={{ color: "#e63946" }}
                                    size={24}
                                  />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <IconButton size="small">
                              <CircularProgress color="success" size={24} />
                            </IconButton>
                          ))}
                        {pathology?.report?.length > 0 && (
                          <sub style={{ marginLeft: "auto" }}>
                            Normal report
                          </sub>
                        )}
                      </Box>
                      {pathology?.report?.length > 0 &&
                        pathology?.widalReport?.data?.length > 0 && <Divider />}
                      {pathology?.widalReport?.data?.length > 0 && (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Tooltip
                            title="Edit Widal Report"
                            arrow
                            placement="top"
                          >
                            <IconButton size="small">
                              <EditIcon
                                color="primary"
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleEdit(pathology, "widal")}
                              />
                            </IconButton>
                          </Tooltip>
                          {/* <Tooltip
                            title="Delete Widal Report"
                            arrow
                            placement="top"
                          >
                            <IconButton size="small">
                              <Delete
                                color="primary"
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleEdit(pathology, "widal")}
                              />
                            </IconButton>
                          </Tooltip> */}
                          {pathology.widalReportFileUrl?.length === 0 ? (
                            pathology.overallWidalStatus === 1 ? (
                              !loadingStates[`${pathology._id}_widal`] ? (
                                <Tooltip
                                  title="Upload Widal Report"
                                  arrow
                                  placement="top"
                                >
                                  <IconButton size="small">
                                    <UploadFileIcon
                                      color="success"
                                      sx={{ cursor: "pointer" }}
                                      onClick={() =>
                                        handleGenerateWidalPDF(pathology)
                                      }
                                    />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <IconButton size="small">
                                  <CircularProgress color="success" size={24} />
                                </IconButton>
                              )
                            ) : (
                              ""
                            )
                          ) : !loadingStates[`${pathology._id}_widal`] ? (
                            <>
                              <Tooltip
                                title="Print Widal with template"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => viewWidalReport(pathology)}
                                >
                                  <NoteAddIcon
                                    sx={{ color: "#2a9d8f" }}
                                    size={24}
                                  />
                                </IconButton>
                              </Tooltip>

                              <Tooltip
                                title="Print Widal without template"
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleGenerateWidalPDF(pathology, 0)
                                  }
                                >
                                  <ContentCopyIcon
                                    sx={{ color: "#e63946" }}
                                    size={24}
                                  />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <IconButton size="small">
                              <CircularProgress color="success" size={24} />
                            </IconButton>
                          )}
                          <sub style={{ marginLeft: "auto" }}>Widal report</sub>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      {pathology?.report?.length > 0 && (
                        <Box>
                          <Tooltip
                            title={
                              getColorTitle(pathology?.overallNormalStatus)[0]
                            }
                            arrow
                            placement="right"
                            // open
                          >
                            <IconButton>
                              <CircleIcon
                                color={
                                  getColorTitle(
                                    pathology?.overallNormalStatus,
                                  )[1]
                                }
                                sx={{ cursor: "pointer", fontSize: "15px" }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}

                      {pathology?.widalReport?.data?.length > 0 && (
                        <Box>
                          <Tooltip
                            title={
                              getColorTitle(pathology?.overallWidalStatus)[0]
                            }
                            arrow
                            placement="right"
                            // open
                          >
                            <IconButton>
                              <CircleIcon
                                color={
                                  getColorTitle(
                                    pathology?.overallWidalStatus,
                                  )[1]
                                }
                                sx={{ cursor: "pointer", fontSize: "15px" }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} sx={{ padding: 0 }}>
                  <img
                    src={noData}
                    style={{ margin: "auto" }}
                    width="50%"
                    height="50%"
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Log
        openLogDialog={openLogDialog}
        handleCloseLog={handleCloseLog}
        logs={invoiceLogs}
      />
    </Box>
  );
};

export default PathologyReports;
